<template>
  <f7-page :page-content="false">

    <!-- NAVBAR para selector de herramienta -->
    <f7-navbar v-if="!herramienta">
      <f7-nav-left>
        <f7-link @click="volverAtras" icon-f7="chevron_left">{{ $t('atras') }}</f7-link>
      </f7-nav-left>
      <f7-nav-title>Seleccionar Herramienta</f7-nav-title>
    </f7-navbar>

    <!-- SELECTOR DE HERRAMIENTA -->
    <f7-page-content v-if="!herramienta" class="padding">
      <div style="padding:56px 12px 12px">
        <h2 style="font-size:14px;font-weight:700;color:#1a2744;margin-bottom:12px">Seleccionar Herramienta</h2>
        <!-- Mientras se recuperan las versiones del cálculo de la línea no se ofrece elegir otra
             herramienta: al llegar, el cálculo reabierto se pondría encima de lo elegido (#467). -->
        <p v-if="reabriendo" style="font-size:13px;color:#64748b">Recuperando el cálculo guardado en la línea…</p>
        <div v-else class="sel-grid">
          <button v-for="h in herramientas" :key="h.codigo" class="sel-card" @click="selectHerramienta(h)">
            <span style="font-size:22px">{{ h.icono||'📐' }}</span>
            <b>{{ h.nombre }}</b>
            <span style="font-size:9px;color:#94a3b8;text-transform:uppercase">{{ h.familia }}</span>
            <span style="font-size:9px;color:#999">v.{{ h.version||1 }}<template v-if="h.fechaDesde"> · {{ formatFechaSel(h.fechaDesde) }}</template></span>
          </button>
        </div>
      </div>
    </f7-page-content>

    <!-- NAVBAR para herramienta seleccionada (diseño actual; el nuevo trae su propia cabecera) -->
    <f7-navbar v-if="herramienta && !disenoNuevo">
      <f7-nav-left>
        <f7-link @click="volverAtras" icon-f7="chevron_left">{{ $t('atras') }}</f7-link>
        <span v-if="herramienta" style="font-size:10px;color:rgba(255,255,255,.7);margin-left:8px;white-space:nowrap">
          v{{ herramienta.version || 1 }} · {{ herramienta.fechaDesde ? new Date(herramienta.fechaDesde).toLocaleDateString('es-ES') : '' }}{{ herramienta.fechaHasta ? ' → ' + new Date(herramienta.fechaHasta).toLocaleDateString('es-ES') : ' → Vigente' }}
        </span>
      </f7-nav-left>
      <f7-nav-title>{{ herramienta.icono }} {{ herramienta.nombre }}</f7-nav-title>
      <f7-nav-right>
        <f7-link class="tb-nav" style="background:#2563eb" @click="openToolbar('INFO')">Info</f7-link>
        <f7-link class="tb-nav" style="background:#dc2626" @click="openToolbar('NORM')">Norma</f7-link>
        <f7-link class="tb-nav" style="background:#16a34a" @click="openToolbar('DOCM')">Docs</f7-link>
        <f7-link class="tb-nav" style="background:#7c3aed" @click="openToolbar('CON')">Conoc</f7-link>
        <f7-link class="tb-nav" style="background:#6d28d9" @click="tutorOpen=true">Tutor IA</f7-link>
        <!-- TODO: Boton "Guardar" en modo standalone (sin oferta). Comentado mientras se valida con cliente:
             el endpoint /tarifas/calcular-y-guardar pertenece al motor antiguo (TarifaMO) y exige tarifaId,
             que el calculador v3 (basado en TarifaHerramientaMO) no envia. Si se decide registrar logs de
             calculo en standalone, hay que crear un endpoint v3 nuevo o adaptar este. -->
        <!-- <f7-link v-roles="['ADMIN']" v-if="!mostrarContexto" class="tb-nav" style="background:#E8901A" @click="accionGuardar">Guardar</f7-link> -->
        <f7-link class="tb-nav" style="background:rgba(255,255,255,.15);color:rgba(255,255,255,.7)" @click="generarPDF">PDF</f7-link>
        <f7-link v-if="mostrarContexto && herramienta && res && res._p1" class="tb-nav" style="background:#16a34a" @click="accionGuardar">Guardar en Linea</f7-link>
        <button class="tb-guia" @click="openGuiaUso" title="Guía de Uso del Motor Tarifas">i</button>
      </f7-nav-right>
    </f7-navbar>

    <!-- DISEÑO NUEVO (#467): el de la maqueta aprobada el 09/10/2026. Convive con el actual y se elige
         por herramienta en `usaDisenoNuevo`; hoy lo usan RITE, Baja Tensión (#961) y Comunidades de
         propietarios (#1166). El cálculo, el formulario y lo que se guarda en la línea son los mismos
         para los dos: lo que cambia es cómo se pinta. Lo único que cambia de una herramienta a otra es
         el bloque de sus datos de entrada. -->
    <CalculadoraDiseno v-if="herramienta && disenoNuevo"
      :titulo="herramienta.nombre" :subtitulo="subtituloDiseno"
      :puede-guardar="mostrarContexto" :guardar-listo="hayPrecio" :motivo-guardar="motivoGuardar"
      :puede-cambiar="mostrarContexto" @cambiar="cambiarHerramienta"
      @atras="volverAtras" @info="openToolbar('INFO')" @norma="openToolbar('NORM')" @docs="openToolbar('DOCM')"
      @conoc="openToolbar('CON')" @tutor="tutorOpen = true" @pdf="generarPDF" @guia="openGuiaUso" @guardar="pedirGuardado">
      <CalcOfertaLinea v-if="mostrarContexto" :ctx="ctx" @set="setCtx" />
      <CalcVersiones v-if="reapertura" :estado="reapertura.estado" :guardadas="versionesTexto.guardadas" :vigentes="versionesTexto.vigentes"
        :precio-guardado="reapertura.precioGuardado != null ? fmtEur(reapertura.precioGuardado) : ''" @recalcular="recalcularConVigentes" />
      <RiteInstalacion v-if="esRite" :herramienta="herramienta" :form="form" :srv="srv || ''" :fam="riteFam" :matriz="riteMatriz"
        :parametros="riteParametros" :alertas="alertas" :otros="riteOtros" @set="setF" @srv="setSrv" />
      <CalcDatosHerramienta v-else :tipos="tiposInspeccion" :srv="srv || ''" :descripcion="srvDesc" :secciones="visibleSecciones"
        :form="form" :visible="isVisible" @set="setF" @srv="setSrv" @fila="toggleTableRow" />
      <CalcEstadoDatos :faltan="faltanDatos" :resumen="resumenDatos" :avisos="esRite ? [] : alertas" />
      <CalcDesplazamientoGastos :form="form" :cfg="CFG" :kmmin="kmminEfectivo" :tiene2a="tiene2a" :sugerencia="suggManut"
        @set="setF" @copiar="copiarGastos" />
      <template v-slot:resultado>
        <CalcResultado :titulo="srvNombre || herramienta.nombre" :fuente="esRite ? riteFuente : srvNorma" :vista="vistaResultado"
          :puede-guardar="mostrarContexto" :guardar-listo="hayPrecio" :motivo-guardar="motivoGuardar"
          @pdf="generarPDF" @guardar="pedirGuardado" />
      </template>
      <template v-slot:extra>
        <CalcGuardarDialogo :abierto="dialogoGuardar" :titulo="tituloGuardado" :filas="filasGuardado" :faltan="faltanDatos"
          @cancelar="cancelarGuardado" @confirmar="confirmarGuardado" />
      </template>
    </CalculadoraDiseno>

    <!-- MOTOR UNIVERSAL -->
    <div v-if="herramienta && !disenoNuevo" class="v3">

      <!-- BODY -->
      <div class="main">
        <div class="col-left">
          <div class="cols2">

            <!-- COL-A: Subtipos + Secciones dinámicas + Recargos -->
            <div class="col-a">

              <!-- Subtipos (si la herramienta los tiene) -->
              <div class="cd" v-if="subtiposKeys.length">
                <div class="ch ch-navy">{{ herramienta.icono }} Tipo de Instalación</div>
                <div class="cb">
                  <div class="sg">
                    <button v-for="k in subtiposKeys" :key="k" class="sc" :class="{act:srv===k}" @click="setSrv(k)">
                      <span class="sc-ico">{{ subtiposMap[k].icono }}</span>
                      <span class="sc-name">{{ subtiposMap[k].nombre }}</span>
                      <span class="sc-norma">{{ subtiposMap[k].norma }}</span>
                    </button>
                  </div>
                  <div v-if="srvDesc" class="info-box">{{ srvDesc }}</div>
                </div>
              </div>

              <!-- Secciones dinámicas desde herramienta.secciones -->
              <div class="cd" v-for="(sec,sIdx) in visibleSecciones" :key="'s'+sIdx">
                <div class="ch ch-navy" v-if="sec.titulo">{{ sec.titulo }}</div>
                <div class="cb">
                  <!-- Alertas -->
                  <div v-if="sIdx===0 && alertas.length" class="aw"><div v-for="(a,ai) in alertas" :key="ai">{{ a }}</div></div>
                  <!-- Widgets dinámicos -->
                  <div :class="'g'+(sec.gridCols||3)">
                    <template v-for="(w,wIdx) in sec.widgets">
                      <!-- NUMBER -->
                      <div class="fi" v-if="w.tipo==='number' && isVisible(w)" :key="'w'+sIdx+'-'+wIdx">
                        <label>{{ w.label }} <span v-if="w.required" class="rq">*</span></label>
                        <input type="number" :value="form[w.campo]" :min="w.min!=null?w.min:0" :step="w.step||1" @input="setF(w.campo,parseFloat($event.target.value)||0)" />
                        <span v-if="w.hint" class="ht">{{ w.hint }}</span>
                      </div>
                      <!-- CHECKBOX -->
                      <div class="ck" v-if="w.tipo==='checkbox' && isVisible(w)" :key="'c'+sIdx+'-'+wIdx" @click="setF(w.campo,!form[w.campo])">
                        <input type="checkbox" :checked="!!form[w.campo]" />{{ w.icono||'' }} {{ w.label }}
                      </div>
                      <!-- SELECT -->
                      <div class="fi" v-if="w.tipo==='select' && isVisible(w)" :key="'sel'+sIdx+'-'+wIdx">
                        <label>{{ w.label }}</label>
                        <select :value="form[w.campo]" @change="setF(w.campo,$event.target.value)">
                          <option v-for="o in w.options" :key="o.key" :value="o.key">{{ o.label||o.nombre }}</option>
                        </select>
                      </div>
                      <!-- TOGGLE PAIR -->
                      <div class="fi" v-if="w.tipo==='togglePair' && isVisible(w)" :key="'tp'+sIdx+'-'+wIdx">
                        <label>{{ w.label }}</label>
                        <div style="display:flex;gap:3px">
                          <button v-for="o in w.options" :key="o.key" class="sc" :class="{act:form[w.campo]===o.key}" style="flex:1;padding:4px" @click="setF(w.campo,o.key)">
                            <span class="sc-name">{{ o.label||o.nombre }}</span>
                          </button>
                        </div>
                      </div>
                      <!-- RADIO CARD -->
                      <div class="fi" v-if="w.tipo==='radioCard' && isVisible(w)" :key="'rc'+sIdx+'-'+wIdx" :style="w.fullWidth?'grid-column:1/-1':''">
                        <label>{{ w.label }}</label>
                        <div :class="'g'+(w.cols||2)" style="margin-top:3px">
                          <button v-for="o in w.options" v-if="!o.visible || isVisible(o)" :key="o.key" class="sc" :class="{act:form[w.campo]===o.key}" @click="setF(w.campo,o.key)">
                            <span v-if="o.icono" class="sc-ico">{{ o.icono }}</span>
                            <span class="sc-name">{{ o.label||o.nombre }}</span>
                            <span v-if="o.sub" class="sc-norma">{{ o.sub }}</span>
                          </button>
                        </div>
                      </div>
                      <!-- LEVEL SELECTOR -->
                      <div class="fi" v-if="w.tipo==='levelSelector' && isVisible(w)" :key="'ls'+sIdx+'-'+wIdx" style="grid-column:1/-1">
                        <label>{{ w.label }}</label>
                        <div style="display:flex;gap:4px;margin-top:3px">
                          <button v-for="o in w.options" :key="o.key" class="sc" :class="{act:form[w.campo]===o.key}" :style="form[w.campo]===o.key?'background:'+o.color+';color:#fff;border-color:'+o.color:''" style="flex:1;padding:6px" @click="setF(w.campo,o.key)">
                            <span class="sc-name">{{ o.label }}</span>
                          </button>
                        </div>
                      </div>
                      <!-- CHECKBOX CARD -->
                      <div v-if="w.tipo==='checkboxCard' && isVisible(w)" :key="'cc'+sIdx+'-'+wIdx" class="ck" style="flex-direction:column;align-items:stretch" @click.self="setF(w.campo,!form[w.campo])">
                        <div style="display:flex;align-items:center;gap:4px" @click="setF(w.campo,!form[w.campo])">
                          <input type="checkbox" :checked="!!form[w.campo]" />{{ w.icono||'' }} {{ w.label }}
                        </div>
                        <div v-if="form[w.campo] && w.subInput" style="margin-top:4px;padding-top:4px;border-top:1px solid #eee">
                          <label style="font-size:7px;color:#888">{{ w.subInput.label }}</label>
                          <input type="number" :value="form[w.subInput.campo]" :min="w.subInput.min||0" :step="w.subInput.step||1" style="width:100%" @input.stop="setF(w.subInput.campo,parseFloat($event.target.value)||0)" @click.stop />
                        </div>
                      </div>
                      <!-- TABLE CHECKBOXES -->
                      <div v-if="w.tipo==='tableCheckboxes' && isVisible(w)" :key="'tc'+sIdx+'-'+wIdx" style="grid-column:1/-1">
                        <label>{{ w.label }}</label>
                        <div style="max-height:200px;overflow-y:auto;border:1px solid #e2e8f0;border-radius:4px;margin-top:3px">
                          <table style="width:100%;border-collapse:collapse;font-size:8px">
                            <thead><tr style="background:#f1f5f9"><th style="padding:3px;text-align:left;width:30px"></th><th style="padding:3px;text-align:left">Sistema</th><th style="padding:3px;text-align:right;width:60px">TPC</th></tr></thead>
                            <tbody>
                              <tr v-for="row in w.rows" :key="row.key" style="border-bottom:1px solid #f0f0f0;cursor:pointer" @click="toggleTableRow(w.campo,row.key)">
                                <td style="padding:3px"><input type="checkbox" :checked="form[w.campo] && form[w.campo][row.key]" style="accent-color:#E8901A" /></td>
                                <td style="padding:3px">{{ row.label }}</td>
                                <td style="padding:3px;text-align:right;color:#E8901A;font-weight:700">{{ form[w.campo] && form[w.campo][row.key] ? row.tpcLabel||'✓' : '' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Recargos (siempre) -->
              <div class="cd cd-grow">
                <div class="ch ch-brown">⏰ Recargos</div>
                <div class="cb">
                  <div class="g2">
                    <div class="ck" @click="setF('recargoFS',!form.recargoFS)"><input type="checkbox" :checked="form.recargoFS" />📅 Fin de semana (+{{ CFG.pctFS }}%)</div>
                    <div class="ck" @click="setF('recargoNocturno',!form.recargoNocturno)"><input type="checkbox" :checked="form.recargoNocturno" />🌙 H. nocturnas (+{{ CFG.pctNocturno }}%)</div>
                  </div>
                  <div v-if="form.recargoFS||form.recargoNocturno" class="ai">Sobre BASE (trabajo+km).<span v-if="form.recargoFS&&form.recargoNocturno"> Acumulables: {{ CFG.pctFS+CFG.pctNocturno }}%</span></div>
                </div>
              </div>
            </div>

            <!-- COL-B: Desplazamiento + Gastos -->
            <div class="col-b">
              <!-- Desplazamiento -->
              <div class="cd">
                <div class="ch ch-green">🚗 Desplazamiento</div>
                <div class="cb">
                  <div class="ai">Km mín: <b>{{ kmminEfectivo }}</b> · T.desp = km×<b>{{ CFG.tdf }}</b> min/km · Coste = km×<b>{{ CFG.kmcost }} €/km</b></div>
                  <div class="g3" style="margin-top:5px">
                    <div class="fi"><label>Km (ida+vuelta)</label><input type="number" :value="form.km" min="0" step="5" :disabled="form.mismo" @input="setF('km',parseFloat($event.target.value)||0)" /></div>
                    <div class="fi"><label>Descuento 1ª</label><input type="number" :value="form.dto" min="0" max="1" step="0.05" @input="setF('dto',parseFloat($event.target.value)||0)" /></div>
                    <div class="fi" v-if="tiene2a"><label>Descuento 2ª</label><input type="number" :value="form.dto2" min="0" max="1" step="0.05" @input="setF('dto2',parseFloat($event.target.value)||0)" /></div>
                  </div>
                  <div style="margin-top:4px"><div class="ck" @click="setF('mismo',!form.mismo)"><input type="checkbox" :checked="form.mismo" />📍 Mismo Emplaz.</div></div>
                </div>
              </div>
              <!-- Gastos 1ª -->
              <div class="cd">
                <div class="ch ch-blue">💶 Gastos 1ª Inspección</div>
                <div class="cb">
                  <div v-if="suggManut" class="aw">{{ suggManut }}</div>
                  <div class="g2">
                    <div class="fi"><label>Subcontrat. (€)</label><input type="number" :value="form.sub" min="0" @input="setF('sub',parseFloat($event.target.value)||0)" /></div>
                    <div class="fi"><label>Peajes/Parking (€)</label><input type="number" :value="form.peaj" min="0" @input="setF('peaj',parseFloat($event.target.value)||0)" /></div>
                  </div>
                  <div class="sep"></div>
                  <div class="stit">Manutención</div>
                  <div class="g4">
                    <div class="fi"><label>Comidas</label><div class="uf"><input type="number" :value="form.ncom" min="0" @input="setF('ncom',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                    <div class="fi"><label>Cenas</label><div class="uf"><input type="number" :value="form.ncen" min="0" @input="setF('ncen',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                    <div class="fi"><label>Hoteles</label><div class="uf"><input type="number" :value="form.nhot" min="0" @input="setF('nhot',parseFloat($event.target.value)||0)" /><span>noche</span></div></div>
                    <div class="fi"><label>Desayunos</label><div class="uf"><input type="number" :value="form.ndes" min="0" @input="setF('ndes',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                  </div>
                </div>
              </div>
              <!-- Gastos 2ª -->
              <div class="cd cd-grow" v-if="tiene2a">
                <div class="ch ch-gray" style="display:flex;justify-content:space-between;align-items:center">
                  <span style="white-space:nowrap;flex:1">💶 Gastos 2ª Inspección</span>
                  <button class="bcopy" style="font-size:7px;padding:2px 5px" @click="copiarGastos">📋 Copiar de 1ª</button>
                </div>
                <div class="cb">
                  <div class="g2">
                    <div class="fi"><label>Subcontrat. (€)</label><input type="number" :value="form.sub2" min="0" @input="setF('sub2',parseFloat($event.target.value)||0)" /></div>
                    <div class="fi"><label>Peajes/Parking (€)</label><input type="number" :value="form.peaj2" min="0" @input="setF('peaj2',parseFloat($event.target.value)||0)" /></div>
                  </div>
                  <div class="g4" style="margin-top:5px">
                    <div class="fi"><label>Comidas</label><div class="uf"><input type="number" :value="form.ncom2" min="0" @input="setF('ncom2',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                    <div class="fi"><label>Cenas</label><div class="uf"><input type="number" :value="form.ncen2" min="0" @input="setF('ncen2',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                    <div class="fi"><label>Hoteles</label><div class="uf"><input type="number" :value="form.nhot2" min="0" @input="setF('nhot2',parseFloat($event.target.value)||0)" /><span>noche</span></div></div>
                    <div class="fi"><label>Desayunos</label><div class="uf"><input type="number" :value="form.ndes2" min="0" @input="setF('ndes2',parseFloat($event.target.value)||0)" /><span>ud</span></div></div>
                  </div>
                </div>
              </div>
              <!-- Panel Contexto Oferta -->
              <div v-if="contextoOferta || mostrarContexto" class="ctx-panel">
                <div class="ctx-hdr" @click="ctxOpen=!ctxOpen"><span>📋 DATOS DE LA OFERTA</span><span style="font-size:12px">{{ ctxOpen ? '▲' : '▼' }}</span></div>
                <div v-show="ctxOpen" class="ctx-body"><div class="ctx-grid">
                  <div class="ctx-field ctx-ro"><label>Cliente</label><input :value="ctx.codCliente + (ctx.nombreCliente ? ' — ' + ctx.nombreCliente : '')" readonly></div>
                  <div class="ctx-field ctx-ro"><label>Oferta</label><input :value="ctx.codOferta" readonly></div>
                  <div class="ctx-field ctx-ro"><label>Linea</label><input :value="ctx.numLinea" readonly></div>
                  <div class="ctx-field ctx-ro"><label>Servicio</label><input :value="ctx.nombreServicio" readonly></div>
                  <div class="ctx-field ctx-ro"><label>Materia</label><input :value="ctx.nombreMateria" readonly></div>
                  <div class="ctx-field ctx-ro"><label>Empleado</label><input :value="ctx.empleadoContratacion" readonly></div>
                  <div class="ctx-field ctx-ed"><label>Subcontrata (cod)</label><input :value="ctx.codSubcontrata" @input="ctx.codSubcontrata=$event.target.value"></div>
                  <div class="ctx-field ctx-ed"><label>Subcontrata (nombre)</label><input :value="ctx.nombreSubcontrata" @input="ctx.nombreSubcontrata=$event.target.value"></div>
                  <div class="ctx-field ctx-ro"><label>Pedido</label><input :value="ctx.codPedido || '—'" readonly></div>
                </div></div>
              </div>
            </div>
          </div>
        </div>

        <!-- PANEL RESULTADO -->
        <div class="col-right">
          <div class="rh">
            <div class="rh-t">{{ herramienta.icono }} RESULTADO {{ herramienta.nombre }}</div>
            <div class="rh-s">{{ srvNombre || herramienta.subtitulo || '' }}</div>
            <div class="rh-s" style="font-size:7px;color:rgba(255,255,255,.25);margin-top:2px">v{{ herramienta.version||1 }} · Config v{{ CFG.version||1 }}</div>
          </div>

          <!-- Resultado CLASIFICACIÓN -->
          <div v-if="res.tipo === 'clasificacion'" class="r-body" style="padding:14px">
            <div style="text-align:center;margin-bottom:12px">
              <div style="font-size:10px;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.1em">Ps × V</div>
              <div style="font-size:36px;font-weight:800;color:#E8901A">{{ fN(res.psxv) }}</div>
              <div style="font-size:10px;color:rgba(255,255,255,.5)">bar · L</div>
            </div>
            <div v-if="res.sinOCA" style="padding:10px;background:rgba(22,163,74,.15);border:1px solid rgba(22,163,74,.3);border-radius:6px;text-align:center;margin-bottom:12px">
              <div style="font-size:13px;font-weight:700;color:#4ade80">Sin obligacion OCA</div>
              <div style="font-size:10px;color:rgba(255,255,255,.5)">Ps×V &lt; 51 bar·L</div>
            </div>
            <div v-else-if="res.psxv === 0" style="text-align:center;color:rgba(255,255,255,.4);font-size:11px;padding:20px">Introduce Ps y V para clasificar</div>
            <div v-else-if="res.categoria" style="padding:10px;background:rgba(255,255,255,.06);border-radius:6px">
              <div style="text-align:center;margin-bottom:10px">
                <div style="font-size:10px;color:rgba(255,255,255,.4);text-transform:uppercase">Categoria</div>
                <div style="font-size:28px;font-weight:800;color:#fff">{{ res.categoria.cat }}</div>
                <div style="font-size:10px;color:rgba(255,255,255,.5)">Rango: {{ res.categoria.rango }}</div>
              </div>
              <div style="font-size:11px;color:rgba(255,255,255,.7)">
                <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid rgba(255,255,255,.06)"><span>Nivel A (en servicio)</span><b>{{ res.categoria.a ? 'Cada ' + res.categoria.a + ' anos' : '—' }}</b></div>
                <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid rgba(255,255,255,.06)"><span>Nivel B (apertura)</span><b>{{ res.categoria.b ? 'Cada ' + res.categoria.b + ' anos' : '—' }}</b></div>
                <div style="display:flex;justify-content:space-between;padding:4px 0"><span>Nivel C (prueba)</span><b>{{ res.categoria.c ? 'Cada ' + res.categoria.c + ' anos' : '—' }}</b></div>
              </div>
            </div>
          </div>

          <!-- Resultado NORMAL (formula / manual / tablaPrecio) -->
          <div v-else class="r-body">
            <!-- Estado de los datos: va ARRIBA porque tiene que leerse antes que el precio (#466) -->
            <div v-if="res.miss && res.miss.length" class="r-miss">
              <div class="r-miss-t">Faltan datos para dar el precio por bueno</div>
              <div v-for="(m,mi) in res.miss" :key="'miss'+mi" class="r-miss-l">{{ m }}</div>
            </div>
            <div class="trow">
              <div class="tb"><div class="tl">TPC</div><div class="tv">{{ res.tpc||'—' }}</div><div class="tu">min</div></div>
              <div class="tb"><div class="tl">TPI</div><div class="tv">{{ res.tpi||'—' }}</div><div class="tu">min</div></div>
              <div class="tb"><div class="tl">T.DESP</div><div class="tv">{{ res.td||'—' }}</div><div class="tu">min</div></div>
              <div class="tb t-acc"><div class="tl">TOTAL</div><div class="tv t-big">{{ res.ttot||'—' }}</div><div class="tu">min</div></div>
            </div>
            <div class="tinfo"><span>Horas: <b>{{ res.horas||'—' }}</b></span><span>TPI%: <b>{{ res.tpce||'—' }}</b></span><span v-if="srvNorma">Norma: <b>{{ srvNorma }}</b></span></div>
            <!-- 1ª -->
            <div class="rp rp-1">
              <div class="rp-lbl">1ª INSPECCIÓN</div>
              <div class="rp-price">{{ res.p1Fmt||'— €' }}</div>
              <div class="rp-dg">
                <div class="dl"><span>Trabajo × dto</span><span>{{ res.p1trab }}</span></div>
                <div class="dl"><span>Km (Ida+Vuelta)</span><span>{{ res.kmLabel }}</span></div>
                <div class="dl" v-if="res.rFS1"><span>Recargo FS ({{ CFG.pctFS }}%)</span><span>{{ fmtEur(res.rFS1) }}</span></div>
                <div class="dl" v-if="res.rN1"><span>Rec. Noct. ({{ CFG.pctNocturno }}%)</span><span>{{ fmtEur(res.rN1) }}</span></div>
                <div class="dl" v-for="(g,gi) in res.gastos1" :key="'g1'+gi" v-if="g.v"><span>{{ g.k }}</span><span>{{ fmtEur(g.v) }}</span></div>
                <div class="dl dl-t"><span>TOTAL 1ª</span><span>{{ res.p1Fmt }}</span></div>
              </div>
            </div>
            <div class="r-sep"></div>
            <!-- 2ª -->
            <div class="rp" v-if="tiene2a">
              <div class="rp-lbl">2ª INSPECCIÓN <span class="pill">{{ res.pct||'—%' }}</span></div>
              <div class="rp-price rp-2">{{ res.p2Fmt||'— €' }}</div>
              <div class="rp-dg">
                <div class="dl"><span>TPC2 / TPI2</span><span>{{ res.tpc2Label }}</span></div>
                <div class="dl"><span>Trabajo 2ª × dto</span><span>{{ res.p2trab }}</span></div>
                <div class="dl"><span>Km (Ida+Vuelta)</span><span>{{ res.kmLabel }}</span></div>
                <div class="dl" v-if="res.rFS2"><span>Recargo FS ({{ CFG.pctFS }}%)</span><span>{{ fmtEur(res.rFS2) }}</span></div>
                <div class="dl" v-if="res.rN2"><span>Rec. Noct. ({{ CFG.pctNocturno }}%)</span><span>{{ fmtEur(res.rN2) }}</span></div>
                <div class="dl" v-for="(g,gi) in res.gastos2" :key="'g2'+gi" v-if="g.v"><span>{{ g.k }}</span><span>{{ fmtEur(g.v) }}</span></div>
                <div class="dl dl-t dl-t2"><span>TOTAL 2ª</span><span>{{ res.p2Fmt }}</span></div>
              </div>
            </div>
            <!-- Visita fallida: se enseña SIEMPRE debajo del resultado, con su desglose (#466). No es
                 una alternativa al precio: es la partida que se cobra si el dia de la visita no hay
                 frigorista, y el comercial tiene que poder avisarlo en la oferta sin calcularla aparte. -->
            <template v-if="res.failD">
              <div class="r-sep"></div>
              <div class="rp">
                <div class="rp-lbl">VISITA FALLIDA</div>
                <div class="rp-price rp-f">{{ fmtEur(res.fail) }}</div>
                <div class="rp-dg">
                  <!-- Los minutos improductivos salen del motor (`failD.imp`) y no escritos aquí: si
                       Dirección los cambia en la configuración de la herramienta, este desglose
                       contaría una operación distinta de la que ha dado el total. -->
                  <div class="dl"><span>Tiempo ({{ fMin(res.failD.min) }} min: {{ fMin(res.failD.desp) }} de desplazamiento + {{ fMin(res.failD.imp) }} improductivos)</span><span>{{ fmtEur(res.failD.tiempo) }}</span></div>
                  <div class="dl"><span>Km (Ida+Vuelta)</span><span>{{ fmtEur(res.failD.km) }}</span></div>
                  <div class="dl"><span>Mínimo</span><span>{{ fmtEur(res.failD.minimo) }}</span></div>
                  <div class="dl dl-t"><span>TOTAL VISITA FALLIDA</span><span>{{ fmtEur(res.fail) }}</span></div>
                </div>
                <div class="rp-note">Ausencia de frigorista o de personal cualificado. No incluye gastos de dietas.</div>
              </div>
            </template>
          </div><!-- /v-else r-body -->

          <div class="r-foot">
            <!-- TODO: en modo standalone (sin oferta) este boton llamaba a guardarCalculo() y rompia
                 contra /tarifas/calcular-y-guardar (mismatch motor antiguo/v3). Restringido a popup.
                 Pendiente de validar con cliente si se rescata el flujo standalone. -->
            <button v-if="mostrarContexto" class="rb rb-p" @click="accionGuardar">💾 Guardar en línea</button>
            <button class="rb rb-s">🖨️ Imprimir</button>
          </div>
        </div>
      </div>
    </div>
    <CalculadorToolbar v-if="herramienta" ref="toolbar" :modulo="herramienta.codigo" :lateral="disenoNuevo" style="display:none" />
    <CalculadorTutor v-if="herramienta" :modulo="herramienta.codigo" :cfg="cfgEfectiva" :open="tutorOpen" @update:open="tutorOpen = $event" />
    <!-- Popup Guia de Uso del Motor Tarifas -->
    <f7-popup ref="popupGuia" :opened="popupGuiaOpened" close-on-escape @popup:closed="popupGuiaOpened=false" class="popup-guia-uso">
      <f7-page>
        <f7-navbar title="📖 Guía de Uso del Motor Tarifas">
          <f7-nav-right>
            <f7-button v-roles="['ADMIN', 'DTYCAL']" v-if="!guiaEditMode" small fill class="btn-editar-guia" icon-f7="pencil" @click="entrarModoEdicion">Editar</f7-button>
            <f7-button v-if="guiaEditMode" small fill class="btn-guardar-guia" icon-f7="checkmark_alt" @click="guardarGuia" :disabled="guiaGuardando">{{ guiaGuardando ? 'Guardando...' : 'Guardar' }}</f7-button>
            <f7-button v-if="guiaEditMode" small class="btn-cancelar-guia" icon-f7="xmark" @click="cancelarEdicion">Cancelar</f7-button>
            <f7-link popup-close v-if="!guiaEditMode">Cerrar</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-page-content class="guia-container">
          <div v-if="guiaLoading" class="guia-loading">
            <f7-preloader></f7-preloader>
            <span style="margin-left:10px">Cargando guía...</span>
          </div>
          <div v-if="guiaError" class="guia-error">{{ guiaError }}</div>
          <div v-if="guiaContenido && !guiaLoading && !guiaEditMode" v-html="guiaContenido.contenido" class="guia-html-render"></div>
          <guia-editor v-if="guiaEditMode" ref="guiaEditor" :contenido-html="guiaEditContenido" :on-save="onGuiaSaved"></guia-editor>
        </f7-page-content>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<script>
import Tarifa from './../../js/db/Tarifa.js';
import TarifaHerramienta from './../../js/db/TarifaHerramienta.js';
import CalculadorToolbar from './components/CalculadorToolbar.vue';
import CalculadorTutor from './components/CalculadorTutor.vue';
import TarifaContenido from './../../js/db/TarifaContenido.js';
import GuiaEditor from './../../components/GuiaEditor.vue';
import RiteMotor from './rite-motor.js';
import ExpresionCondicion from './expresion-condicion.js';
import TarifaMotor from './tarifa-motor.js';
import cerrojoDeLaSesion from './../../js/utils/cerrojoDeLaSesion.js';
import { atarALaSesion } from './../../js/utils/sesionDePeticiones.js';
import DisenoCalculadora from './../../components/tarifas/diseno/index.js';
import RiteInstalacion from './components/RiteInstalacion.vue';
// El logotipo apaisado a color, en base64 sea cual sea su tamaño (`!!url-loader` sin límite): el
// PDF lo incrusta en una ventana en blanco, donde una ruta a `/static/` puede no resolverse.
import logoApaisado from '!!url-loader!./../../static/img/logo-simecal-2026-apaisado.png';

// Valores fijados por direccion el 06/10/2026 (#370); los sustituye la config global al cargar. Fuera
// de `data` porque tambien son la base sobre la que se monta una version antigua al reabrir (#467).
const CFG_POR_DEFECTO = { ph: 107, kmcost: 0.29, kmmin: 60, tdf: 0.65, com: 14, cen: 14, hot: 60, des: 4, pctFS: 30, pctNocturno: 35 };

// Las herramientas de fórmula que ya se pintan con el diseño nuevo, por su código (ver
// `usaDisenoNuevo`): Baja Tensión (#961) y Comunidades de propietarios (#1166).
const DISENO_NUEVO_FORMULA = ['rebt', 'cp'];

// Los estilos del presupuesto impreso de TODAS las herramientas, con los colores de la marca (#468,
// #471). Viven sólo aquí para que la hoja de RITE y la del resto no vuelvan a separarse: un cliente
// no puede recibir dos presupuestos con dos marcas.
const CSS_HOJA_PRESUPUESTO = 'body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#1F2A37;font-size:12px}'
  + '.sheet{max-width:794px;margin:0 auto;padding:36px 44px 30px;box-sizing:border-box}'
  + '.sh-top{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding-bottom:14px;border-bottom:3px solid #4B8A2B}'
  + '.sh-top img{height:46px}'
  + '.sh-meta{text-align:right;font-size:10.5px;color:#5B6B7D;line-height:1.6}.sh-meta b{color:#002653}'
  + '.sh-title{margin:20px 0 2px;font-size:21px;color:#002653;font-weight:700}'
  + '.sh-sub{color:#4B8A2B;font-weight:700;font-size:12.5px}'
  + '.sh-sec{margin:18px 0 6px;font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:#002653;font-weight:800;display:flex;align-items:center;gap:8px}'
  + '.sh-sec::after{content:"";flex:1;height:1px;background:#C9D4E0}'
  + '.sh-t{width:100%;border-collapse:collapse}'
  + '.sh-t td{padding:4px 0;border-bottom:1px solid #E6ECF2}'
  + '.sh-t td:last-child{text-align:right;font-variant-numeric:tabular-nums;font-weight:500;padding-left:12px}'
  + '.sh-times{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}'
  + '.sh-times div{border:1px solid #D9E1EA;border-radius:6px;padding:8px;text-align:center}'
  + '.sh-times small{display:block;font-size:9px;letter-spacing:.1em;color:#5B6B7D;text-transform:uppercase}'
  + '.sh-times b{font-size:17px;color:#002653}'
  + '.sh-times .t{background:#002653;border-color:#002653}.sh-times .t b,.sh-times .t small{color:#fff}'
  + '.sh-prices{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}.sh-prices.una{grid-template-columns:1fr}'
  + '.sh-p{border:1px solid #D9E1EA;border-radius:8px;padding:12px 14px}'
  + '.sh-p.one{border-color:#002653;border-width:2px}'
  + '.sh-p .k{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#5B6B7D;font-weight:700}'
  + '.sh-p .v{font-size:26px;font-weight:700;color:#002653}.sh-p.two .v{color:#4B8A2B}'
  + '.sh-miss{margin-top:12px;padding:8px 10px;border:1px solid #fde68a;background:#fffbeb;border-radius:4px;font-size:10px;color:#92400e}'
  + '.sh-foot{margin-top:24px;padding-top:10px;border-top:1px solid #C9D4E0;display:flex;justify-content:space-between;gap:16px;font-size:9.5px;color:#5B6B7D}.sh-foot b{color:#002653}'
  + '@media print{.sheet{padding:10px 18px}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}}'
  // Cada bloque —su título con lo que lleva debajo— va entero a la página en la que quepa, y el pie
  // va DENTRO del último bloque (#1028). Así la tabla de la 2ª no se parte entre dos páginas y el pie
  // no puede quedarse solo: si no cabe, baja con las condiciones. No depende de cuánto ocupe la hoja,
  // así que sigue valiendo cuando la #1034 añada su leyenda.
  + 'section.sh-blk{display:block;break-inside:avoid;page-break-inside:avoid}';

// Los textos de la hoja pueden venir de Mongo (la etiqueta del rango «> 700 kW», los campos
// añadidos desde el editor): sin escapar, un «<» o un «>» rompe la maqueta del papel.
function escHoja(t) {
  return String(t == null ? '' : t).replace(/[&<>"]/g, function (x) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[x]; });
}

/**
 * Quién está conectado, como lo guarda la sesión: el código es el `username` y el nombre, el que
 * enseña el menú. Sin sesión legible, vacío: «Elaborado por» dirá «—» antes que inventarse a nadie.
 */
function usuarioConectado() {
  let u = {};
  try { u = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch (e) { u = {}; }
  return { codigo: u.username || '', nombre: [u.nombre, u.apellidos].filter(Boolean).join(' ').trim() };
}

/** Una fila etiqueta/valor de la hoja de las herramientas que no son RITE. */
function filaPdfHoja(label, val) {
  return '<tr><td class="dk">' + label + '</td><td class="dv">' + val + '</td></tr>';
}

/** Una casilla de «Tiempos de inspección» de esa misma hoja, con su unidad debajo. */
function tiempoPdfHoja(etiqueta, valor, unidad, clase) {
  return '<div' + (clase ? ' class="' + clase + '"' : '') + '><small>' + etiqueta + '</small><b>' + valor + '</b><i>' + unidad + '</i></div>';
}

// Lo que esa hoja tiene y la de RITE no: las tablas de etiqueta y valor, el importe grande de cada
// inspección con su fila de total y las unidades bajo los tiempos. Va detrás del marco común.
const CSS_HOJA_OTRAS = 'table{width:100%;border-collapse:collapse}'
  + '.dk{padding:4px 8px;border-bottom:1px solid #E6ECF2;color:#5B6B7D;font-size:11px}'
  + '.dv{padding:4px 8px;border-bottom:1px solid #E6ECF2;font-weight:600;font-size:11px;text-align:right;font-variant-numeric:tabular-nums}'
  + '.sh-times i{display:block;font-style:normal;font-size:9px;color:#5B6B7D}.sh-times .t i{color:#fff}'
  + '.hp-pm{font-size:26px;font-weight:700;color:#002653;margin:4px 0 6px}.hp-pm.dos{color:#4B8A2B}'
  + '.hp-tot td{border-top:2px solid #002653;border-bottom:0;font-size:13px;font-weight:700;color:#002653;padding-top:6px}'
  + '.hp-nota{margin-top:2px;font-size:10px;color:#5B6B7D}.hp-pie{margin-top:12px;font-size:9.5px;color:#5B6B7D}';

export default {
  components: Object.assign({ CalculadorToolbar, CalculadorTutor, GuiaEditor, RiteInstalacion }, DisenoCalculadora),
  props: {
    contextoOferta: { type: Object, default: null },
    // El `tarifaCalculo` que ya tiene la línea de oferta, para reabrir el cálculo tal y como quedó
    // (#467). Sólo lo usa el diseño nuevo: con el actual, abrir desde la línea sigue como hasta ahora.
    calculoPrevio: { type: Object, default: null }
  },
  data: function () {
    return {
      herramientas: [],
      herramienta: null,
      srv: null,
      CFG: Object.assign({}, CFG_POR_DEFECTO),
      // La config global vigente tal como llego, aunque `CFG` este fijada a la de un calculo guardado.
      cfgVigente: null,
      // Mientras sea cierto, `CFG` es la version con la que se guardo la linea y la carga de la vigente
      // no la pisa (#467).
      cfgFijada: false,
      configCargada: null,
      // Con que versiones se ha reabierto el calculo de la linea; null si no se ha reabierto ninguno.
      reapertura: null,
      // El formulario tal como quedó al reabrir el cálculo de la línea, para saber si la hoja sigue
      // siendo ese cálculo o ya es otro (#1028, «Elaborado por»). Null si no se ha reabierto.
      formReabierto: null,
      reabriendo: false,
      form: {},
      res: {},
      // Las cifras de la rama de fórmula que pinta el panel del diseño nuevo (#961): el trabajo y los
      // kilómetros de cada visita, que `res` sólo trae ya formateados. Van aparte y no dentro de `res`
      // para que el `res` de todas las herramientas de fórmula siga siendo el de antes, campo a campo
      // (`scripts/prueba-calculador-modos-intactos.js`).
      desglose: null,
      tutorOpen: false,
      alertas: [],
      suggManut: '',
      routeCodigo: null,
      ctxOpen: false,
      mostrarContexto: false,
      ctx: { ofertaId: '', codCliente: '', nombreCliente: '', codOferta: '', numLinea: '', codServicio: '', nombreServicio: '', codMateria: '', nombreMateria: '', empleadoContratacion: '', codSubcontrata: '', nombreSubcontrata: '', codPedido: '', numLineaPedido: '' },
      popupGuiaOpened: false,
      guiaLoading: false,
      guiaContenido: null,
      guiaError: null,
      guiaEditMode: false,
      guiaEditContenido: '',
      guiaGuardando: false,
      // La confirmación de «Guardar en línea» del diseño nuevo: lo que se va a escribir, ya montado.
      dialogoGuardar: false,
      filasGuardado: [],
      resultadoPendiente: null
    };
  },
  watch: {
    // Quien abre la calculadora en una ventana (la oferta) necesita saber qué diseño se pinta: el
    // nuevo ocupa sólo el área de contenido y deja a la vista el menú lateral (#467).
    disenoNuevo: {
      immediate: true,
      handler: function (v) { this.$emit('diseno', v); }
    },
    contextoOferta: {
      immediate: true,
      handler: function (val) {
        if (val) { this.mostrarContexto = true; this.ctxOpen = true; this.ctx = Object.assign({}, this.ctx, val); }
      }
    },
    // Editando la guía no hay «Cerrar»: Escape tampoco la cierra, o se perdería lo escrito sin la
    // pregunta de «Cancelar». Framework7 mira este parámetro en cada pulsación (#1027).
    guiaEditMode: function (editando) {
      const popup = this.$refs.popupGuia;
      if (popup && popup.f7Popup) popup.f7Popup.params.closeOnEscape = !editando;
    }
  },
  computed: {
    subtiposMap: function () {
      if (!this.herramienta || !this.herramienta.subtipos) return {};
      return this.herramienta.subtipos.opciones || {};
    },
    subtiposKeys: function () { return Object.keys(this.subtiposMap); },
    srvDesc: function () { var m = this.subtiposMap[this.srv]; return m ? m.descripcion || '' : ''; },
    srvNombre: function () { var m = this.subtiposMap[this.srv]; return m ? m.nombre || '' : ''; },
    srvNorma: function () { var m = this.subtiposMap[this.srv]; return m ? m.norma || '' : ''; },
    currentParams: function () {
      return TarifaMotor.parametrosDe(this.herramienta, this.srv);
    },
    tiene2a: function () {
      if (!this.herramienta || !this.herramienta.config) return true;
      return this.herramienta.config.tiene2a !== false;
    },
    /**
     * Los kilometros minimos que de verdad se estan cobrando: el override de la herramienta si lo
     * tiene, y si no el global. Lo decide el mismo `TarifaMotor.kmMinimo` con el que calcula
     * `calc()`, y la pantalla lo lee sólo de aquí, porque el minimo se citaba en cuatro sitios mas
     * —la nota de la pantalla, la ficha impresa, lo que se emite a la oferta y el contexto del
     * tutor— y tres de ellos leian el global, de modo que la hoja anunciaba un minimo y el importe
     * venia de otro (#379).
     */
    kmminEfectivo: function () {
      return TarifaMotor.kmMinimo(this.herramienta, this.CFG);
    },
    /**
     * La configuracion que el tutor cita en su prompt. Es la global con el minimo efectivo puesto:
     * el tutor tiene que hablar de la tarifa que se esta cobrando, no de la de otra herramienta.
     */
    cfgEfectiva: function () {
      return Object.assign({}, this.CFG, { kmmin: this.kmminEfectivo });
    },
    visibleSecciones: function () {
      if (!this.herramienta || !this.herramienta.secciones) return [];
      var self = this;
      return this.herramienta.secciones.filter(function (sec) {
        if (!sec.visible) return true;
        return self.isVisible(sec);
      });
    },

    // ═══ DISEÑO NUEVO (#467) ═══
    disenoNuevo: function () { return this.usaDisenoNuevo(this.herramienta); },
    hayPrecio: function () { return !!(this.res && this.res._p1); },
    /** Si la herramienta es RITE: decide qué bloque de datos de entrada y qué desglose se pintan. */
    esRite: function () {
      return !!(this.herramienta && this.herramienta.config && this.herramienta.config.modoCalculo === 'rite');
    },
    /** Los tipos de inspección de la herramienta, en el orden de su definición (#961). */
    tiposInspeccion: function () {
      const ops = this.subtiposMap;
      return Object.keys(ops).map(function (k) {
        const o = ops[k] || {};
        return { clave: k, nombre: o.nombre || o.label || k, norma: o.norma || '', icono: o.icono || '' };
      });
    },
    /**
     * Lo que falta para dar el precio por bueno, lo mismo en «Estado de los datos» y en el diálogo de
     * guardar. En RITE lo dice su motor. En las de fórmula son los campos que la definición marca como
     * obligatorios (`required`, el asterisco del diseño actual) y están a la vista pero vacíos: la
     * fórmula no se queja de ellos —el «Nº Cuadros» vacío lo cuenta como 1—, así que sin esto la
     * pantalla diría «Datos suficientes» con un dato obligatorio sin rellenar (#961).
     */
    faltanDatos: function () {
      if (this.esRite) return (this.res && this.res.miss) || [];
      const self = this, f = this.form || {};
      const faltan = [];
      this.visibleSecciones.forEach(function (sec) {
        (sec.widgets || []).forEach(function (w) {
          if (!w || !w.campo || !w.required || !self.isVisible(w)) return;
          const v = f[w.campo];
          if (v === undefined || v === null || v === '' || v === 0) faltan.push('Falta ' + (w.label || w.campo));
        });
      });
      return faltan;
    },
    /**
     * Por qué «Guardar en línea» está apagado, dicho junto al botón (CLAUDE.md §9). Sólo puede pasar
     * mientras no hay precio, y lo desbloquea quien está calculando, en esta misma pantalla.
     */
    motivoGuardar: function () {
      return this.hayPrecio ? '' : 'Todavía no hay precio que guardar: elige el tipo de inspección y rellena los datos de la instalación; el resultado sale a la derecha.';
    },
    subtituloDiseno: function () {
      const h = this.herramienta || {};
      let vigencia = 'v' + (h.version || 1);
      if (h.fechaDesde) vigencia += ' · desde ' + this.formatFechaSel(h.fechaDesde);
      if (h.fechaHasta) vigencia += ' hasta ' + this.formatFechaSel(h.fechaHasta);
      return ['Motor de tarifas', h.norma || this.srvNorma, vigencia, 'Config global v' + (this.CFG.version || 1)]
        .filter(Boolean).join(' · ');
    },
    riteParametros: function () {
      return RiteMotor.parametros(this.herramienta && this.herramienta.config && this.herramienta.config.rite);
    },
    /** Las inspecciones de la herramienta colocadas por familia y modo, para la matriz. */
    riteMatriz: function () {
      const self = this;
      const ops = this.subtiposMap;
      return Object.keys(ops).map(function (k) {
        const o = ops[k] || {};
        const fm = self.familiaYModo(k, o.params);
        return { clave: k, nombre: o.nombre || o.label || k, fam: fm.fam, mod: fm.mod };
      });
    },
    riteFam: function () {
      return this.familiaYModo((this.form && this.form.srv) || this.srv, this.currentParams).fam;
    },
    riteFuente: function () {
      return { frio: 'Excel de frío', calor: 'Fórmula actual', mixta: 'Excel + actual' }[this.riteFam] || '';
    },
    /**
     * Los widgets de la definición que el diseño de RITE no coloca por su nombre. Hoy no hay ninguno;
     * si se añade uno desde el editor de herramientas, sale en «Otros datos» en vez de desaparecer.
     */
    riteOtros: function () {
      const self = this;
      const conocidos = ['srv', 'sup', 'zon', 'uta', 'emi', 'kw', 'ngc']
        .concat(this.riteParametros.BANDS.map(function (b) { return b.k; }));
      const otros = [];
      this.visibleSecciones.forEach(function (sec) {
        (sec.widgets || []).forEach(function (w) {
          if (!w || !w.campo || conocidos.indexOf(w.campo) >= 0) return;
          if (['number', 'checkbox', 'select'].indexOf(w.tipo) < 0 || !self.isVisible(w)) return;
          otros.push(w);
        });
      });
      return otros;
    },
    resumenDatos: function () {
      if (!this.esRite) return this.resumenFormula();
      const f = this.form || {}, r = this.res || {}, fam = this.riteFam;
      const partes = [];
      if (r.nFrio) partes.push(r.nFrio + ' generador' + (r.nFrio === 1 ? '' : 'es') + ' de frío');
      if (fam !== 'frio') partes.push((f.ngc || 0) + ' de calor');
      if (f.uta && fam !== 'calor') partes.push(f.uta + ' UTA' + (f.uta === 1 ? '' : 's'));
      partes.push((f.sup || 0).toLocaleString('es-ES') + ' m²');
      return partes.join(' · ');
    },
    /**
     * El resultado en la forma que pinta `CalcResultado`. Sólo reordena lo que ya ha calculado
     * `calc()` con `tarifa-motor.js`: ningún importe se vuelve a calcular aquí, para que el panel, el
     * PDF y la línea de oferta no puedan decir cifras distintas.
     */
    vistaResultado: function () {
      if (!this.esRite) return this.vistaFormula();
      const r = this.res || {};
      const c = this.CFG, f = this.form || {}, self = this;
      if (!r.p1) {
        return { tpc: '—', tpi: '—', td: '—', total: '—', horas: '', p1: { total: 0, lineas: [] }, p2: null, minutos: [], fallida: null };
      }
      const pct = function (mult) { return Math.round((1 - (mult || 1)) * 10000) / 100; };
      const coma = function (v) { return String(v).replace('.', ','); };
      const lineas = function (p, dto) {
        const l = [
          { k: 'Trabajo (' + self.fH(p.U) + ', ' + (dto ? '−' + coma(dto) + '%' : 'sin dto.') + ')', v: p.trab },
          { k: 'Kilómetros · ' + r.km + ' km', v: p.kmc }
        ];
        if (p.rfs) l.push({ k: 'Recargo fin de semana ' + c.pctFS + '%', v: p.rfs });
        if (p.rn) l.push({ k: 'Recargo nocturno ' + c.pctNocturno + '%', v: p.rn });
        (p.gs || []).forEach(function (g) { l.push({ k: g[0], v: g[1] }); });
        return l;
      };
      const minutos = (r.why1 || []).map(function (l) { return { k: l[0], v: self.fMin(l[1]) }; });
      minutos.push({ k: 'Desplazamiento · ' + r.km + ' km × ' + coma(c.tdf), v: self.fMin(r.td) });
      return {
        tpc: r.tpc, tpi: r.tpi, td: r.td, total: r.ttot, horas: r.horas,
        p1: { total: r._p1, lineas: lineas(r.p1, pct(f.dto)) },
        p2: this.tiene2a ? { total: r._p2, pct: r.pct2a ? r.pct2a + '% de la 1ª' : '', lineas: lineas(r.p2, pct(f.dto2)) } : null,
        minutos: minutos,
        fallida: r.failD ? {
          total: r.fail,
          lineas: [
            { k: 'Tiempo (' + self.fMin(r.failD.min) + ' min: ' + self.fMin(r.failD.desp) + ' de desplazamiento + ' + self.fMin(r.failD.imp) + ' improductivos)', v: r.failD.tiempo },
            { k: 'Kilómetros · ' + r.km + ' km', v: r.failD.km },
            { k: 'Mínimo', v: r.failD.minimo }
          ],
          nota: 'Si el día de la visita no hay frigorista ni personal cualificado. No incluye gastos de dietas.'
        } : null
      };
    },
    /** Las versiones de la línea y las vigentes, dichas igual en el aviso y en el diálogo de guardar. */
    versionesTexto: function () {
      const r = this.reapertura || {};
      const v = function (x) { return x != null ? 'v' + x : '(sin dato)'; };
      const hVig = r.vigente ? (r.vigente.version || 1) : null;
      const cVig = (this.cfgVigente || this.CFG).version || 1;
      return {
        guardadas: 'herramienta ' + v(r.herramientaVersion) + ' y configuración global ' + v(r.configVersion),
        vigentes: 'herramienta ' + v(hVig) + ' y configuración global ' + v(cVig)
      };
    },
    tituloGuardado: function () {
      const c = this.ctx || {};
      return 'Guardar en la línea ' + (c.numLinea != null && c.numLinea !== '' ? c.numLinea + ' ' : '') + 'de la oferta' + (c.codOferta ? ' ' + c.codOferta : '');
    }
  },
  mounted: function () {
    this.routeCodigo = this.$f7route && this.$f7route.params ? this.$f7route.params.herramientaCodigo || null : null;
    this.configCargada = this.cargarConfig();
    this.cargarHerramientas();
  },
  beforeDestroy: function () {
    // Saliendo con la guía abierta (el botón atrás del navegador, cerrar la ventana de la oferta),
    // Vue la destruiría sin cerrarla y Framework7 dejaría la capa gris encima de la pantalla a la que
    // se vuelve. Los paneles de ayuda y el tutor se cierran solos al irse (#1027).
    if (this.$refs.popupGuia) this.$refs.popupGuia.close(false);
  },
  methods: {
    volverAtras: function () {
      if (this.contextoOferta) {
        this.$emit('close');
      } else if (this.herramienta) {
        this.herramienta = null;
        this.resetForm();
      } else {
        this.$f7router.back();
      }
    },
    formatFechaSel: function(str) {
      if (!str) return '';
      try { var d = new Date(str); return isNaN(d.getTime()) ? str : String(d.getDate()).padStart(2,'0')+'/'+String(d.getMonth()+1).padStart(2,'0')+'/'+d.getFullYear(); } catch(e) { return str; }
    },
    initForm: function () {
      var f = { mismo: false, km: 60, dto: 1, dto2: 1, sub: 0, peaj: 0, ncom: 0, ncen: 0, nhot: 0, ndes: 0, sub2: 0, peaj2: 0, ncom2: 0, ncen2: 0, nhot2: 0, ndes2: 0, recargoFS: false, recargoNocturno: false };
      // Override km with herramienta kmmin
      if (this.herramienta && this.herramienta.config && this.herramienta.config.kmmin) {
        f.km = this.herramienta.config.kmmin;
      }
      // Initialize fields from secciones widgets
      if (this.herramienta && this.herramienta.secciones) {
        this.herramienta.secciones.forEach(function (sec) {
          if (!sec.widgets) return;
          sec.widgets.forEach(function (w) {
            if (!w.campo) return;
            if (w.tipo === 'number') f[w.campo] = w.defaultValue != null ? w.defaultValue : (w.min != null ? w.min : 0);
            else if (w.tipo === 'checkbox' || w.tipo === 'checkboxCard') f[w.campo] = w.defaultValue || false;
            else if (w.tipo === 'select') f[w.campo] = w.defaultValue || (w.options && w.options.length ? w.options[0].key : '');
            else if (w.tipo === 'togglePair' || w.tipo === 'radioCard' || w.tipo === 'levelSelector') f[w.campo] = w.defaultValue || (w.options && w.options.length ? w.options[0].key : '');
            else if (w.tipo === 'tableCheckboxes') f[w.campo] = w.defaultValue || {};
            if (w.subInput && w.subInput.campo) f[w.subInput.campo] = w.subInput.defaultValue != null ? w.subInput.defaultValue : 0;
          });
        });
      }
      this.form = f;
    },
    resetForm: function () {
      this.srv = null;
      this.form = {};
      this.res = {};
      this.desglose = null;
      this.alertas = [];
      this.suggManut = '';
    },
    cargarConfig: function () {
      var self = this;
      return Tarifa.getConfig(true).then(function (r) {
        if (r && r.ph) {
          self.cfgVigente = self.normalizarConfig(r);
          // Reabierto con la version con la que se guardo, la vigente se guarda aparte para el boton
          // de recalcular y no se aplica: aplicarla seria volver a mezclar versiones (#467).
          if (self.cfgFijada) return;
          self.CFG = Object.assign({}, self.cfgVigente);
          // La lista de herramientas puede llegar antes que esta peticion: con una ruta que trae el
          // codigo, `autoSelect` ya habra calculado con los valores de relleno de `CFG` (#379). Los
          // importes viven en `res`, que no se recalcula solo al cambiar `CFG`, asi que sin esto la
          // pantalla se queda con el precio hora de relleno —y lo guarda— mientras la ficha impresa
          // anuncia el que acaba de llegar.
          if (self.herramienta) self.calc();
        }
      }).catch(function () {});
    },
    /** Una version de la config global (la vigente o una del historial) con los numeros como numeros. */
    normalizarConfig: function (r) {
      const p = {};
      Object.keys(r || {}).forEach(function (k) { const v = r[k]; p[k] = typeof v === 'string' && !isNaN(v) ? parseFloat(v) : v; });
      return Object.assign({}, CFG_POR_DEFECTO, p);
    },
    cargarHerramientas: function () {
      var self = this;
      TarifaHerramienta.getList().then(function (l) {
        console.log('[v3] herramientas:', l ? l.length : 0);
        if (l && l.length) self.herramientas = l;
        self.autoSelect();
      }).catch(function (e) {
        console.error('[v3] error:', e);
        self.autoSelect();
      });
    },
    autoSelect: function () {
      if (!this.routeCodigo) return this.reabrirCalculoPrevio();
      var self = this;
      var found = this.herramientas.find(function (h) { return h.codigo === self.routeCodigo; });
      if (found) this.selectHerramienta(found);
    },
    /**
     * Abierta desde una línea que ya tiene un cálculo, vuelve a él: la misma herramienta, todo lo
     * que se tecleó (`tarifaCalculo.parametros`) y las MISMAS versiones de la herramienta y de la
     * config global (`tarifaVersion`, `configGlobalVersion`) (#467, «al reabrirla salen los mismos
     * datos y el mismo resultado»). Con sólo lo tecleado no basta: si el precio hora o la herramienta
     * han cambiado desde que se guardó, la calculadora enseñaría otro precio sin que nadie haya tocado
     * nada, y al confirmar lo escribiría en la oferta.
     *
     * Si esas versiones ya no son las vigentes se piden al historial; si no se pueden recuperar, se
     * calcula con las vigentes y `reapertura` lo dice junto al resultado, con el precio que tiene la
     * línea. Nunca se mezcla una versión guardada con otra vigente: eso no daría ni un precio ni el otro.
     *
     * Sólo para las herramientas del diseño nuevo. Con las demás la pantalla sigue abriendo el
     * selector de herramienta, como hasta ahora: la incidencia pide que no cambien en nada.
     */
    reabrirCalculoPrevio: function () {
      const self = this;
      const p = this.calculoPrevio;
      if (!p || !p.tarifaCodigo || !p.parametros || typeof p.parametros !== 'object') return Promise.resolve();
      const vigente = this.herramientas.find(function (x) { return x.codigo === p.tarifaCodigo; });
      if (!vigente || !this.usaDisenoNuevo(vigente)) return Promise.resolve();
      // #663 · Atada a la sesión: la cadena empieza en una promesa corriente, que recibe la
      // cancelación como rechazo; sin atar, cambiar de cuenta en vuelo dejaba puesto «Recuperando…»
      // y el cálculo de la cuenta anterior podía acabar pintándose en la pantalla de la siguiente.
      cerrojoDeLaSesion(this, 'reabriendo');
      return atarALaSesion(Promise.resolve(this.configCargada)
        .then(function () { return self.versionesGuardadas(vigente, p); }))
        .then(function (v) {
          self.reabriendo = false;
          if (v.cfg) { self.cfgFijada = true; self.CFG = v.cfg; }
          self.reapertura = {
            estado: v.estado, vigente: vigente,
            herramientaVersion: p.tarifaVersion != null ? Number(p.tarifaVersion) : null,
            configVersion: p.configGlobalVersion != null ? Number(p.configGlobalVersion) : null,
            precioGuardado: p.precio1a != null && !isNaN(parseFloat(p.precio1a)) ? parseFloat(p.precio1a) : null
          };
          self.selectHerramienta(v.herramienta, p.parametros);
          // Detrás del `calc()` que `selectHerramienta` deja en cola: lo que éste ajuste en el
          // formulario es parte del cálculo reabierto, no un cambio de quien lo mira.
          self.$nextTick(function () { self.formReabierto = JSON.stringify(self.form); });
        });
    },
    /**
     * Las versiones con las que se reproduce el cálculo guardado `p`:
     *  - `iguales`: las guardadas son las vigentes (o la línea no dice cuáles eran) → las vigentes;
     *  - `guardadas`: alguna ha cambiado y se han recuperado del historial → `herramienta` y `cfg`;
     *  - `irrecuperables`: alguna ha cambiado y no se ha podido recuperar, o la versión guardada de la
     *    herramienta no calcula con este diseño → las vigentes, y la pantalla lo avisa.
     */
    versionesGuardadas: function (vigente, p) {
      const self = this;
      const verH = p.tarifaVersion != null ? Number(p.tarifaVersion) : null;
      const verC = p.configGlobalVersion != null ? Number(p.configGlobalVersion) : null;
      const cambiaH = verH != null && verH !== Number(vigente.version || 1);
      const cambiaC = verC != null && verC !== Number((this.cfgVigente || this.CFG).version || 1);
      if (!cambiaH && !cambiaC) return Promise.resolve({ estado: 'iguales', herramienta: vigente, cfg: null });
      const nada = function () { return null; };
      return atarALaSesion(Promise.all([
        cambiaH ? TarifaHerramienta.getHistorial(vigente.codigo).catch(nada) : null,
        cambiaC ? Tarifa.getConfigHistorial().catch(nada) : null
      ])).then(function (r) {
        const deVersion = function (lista, version) {
          return (Array.isArray(lista) ? lista : []).find(function (x) { return x && Number(x.version || 1) === version; }) || null;
        };
        const h = cambiaH ? deVersion(r[0], verH) : vigente;
        const c = cambiaC ? deVersion(r[1], verC) : null;
        if (!h || (cambiaC && !c) || !self.usaDisenoNuevo(h)) {
          return { estado: 'irrecuperables', herramienta: vigente, cfg: null };
        }
        return { estado: 'guardadas', herramienta: h, cfg: c ? self.normalizarConfig(c) : null };
      });
    },
    /**
     * Deja de calcular con las versiones guardadas en la línea y recalcula lo mismo con las vigentes.
     * La línea no cambia hasta que se pulse «Guardar en línea».
     */
    recalcularConVigentes: function () {
      const r = this.reapertura;
      if (!r || !r.vigente) return;
      const form = JSON.parse(JSON.stringify(this.form));
      this.soltarVersiones();
      this.reapertura = Object.assign({}, r, { estado: 'vigentes' });
      this.selectHerramienta(r.vigente, form);
    },
    /** Vuelve a la config global vigente si estaba fijada la de un cálculo guardado. */
    soltarVersiones: function () {
      if (this.cfgFijada) {
        this.cfgFijada = false;
        this.CFG = Object.assign({}, this.cfgVigente || CFG_POR_DEFECTO);
      }
      this.reapertura = null;
    },
    selectHerramienta: function (h, previos) {
      console.log('[v3] selectHerramienta:', h.codigo, h.nombre);
      this.herramienta = h;
      this.initForm();
      if (previos) {
        this.restaurarFormulario(h, previos);
      } else if (h.subtipos && h.subtipos.opciones) {
        // Set first subtipo
        var keys = Object.keys(h.subtipos.opciones);
        if (keys.length) {
          this.srv = keys[0];
          this.$set(this.form, 'srv', keys[0]);
        }
      }
      this.$nextTick(function () { this.calc(); }.bind(this));
    },
    /**
     * Pone encima del formulario recién inicializado lo que se guardó en la línea. Se parte de
     * `initForm` y no de lo guardado a pelo para que un campo que la herramienta haya ganado
     * después tenga su valor por defecto en vez de quedarse sin definir.
     */
    restaurarFormulario: function (h, previos) {
      const self = this;
      const p = JSON.parse(JSON.stringify(previos));
      Object.keys(p).forEach(function (k) { self.$set(self.form, k, p[k]); });
      const opciones = (h.subtipos && h.subtipos.opciones) || {};
      const claves = Object.keys(opciones);
      // Si la inspección guardada ya no existe en la versión vigente, se cae en la primera, igual
      // que al elegir la herramienta a mano: mejor eso que una matriz sin ninguna marcada.
      const srv = p.srv && opciones[p.srv] ? p.srv : (claves[0] || null);
      if (srv) {
        this.srv = srv;
        this.$set(this.form, 'srv', srv);
      }
    },
    /**
     * EL ÚNICO SITIO donde se decide qué herramienta se pinta con el diseño nuevo (#467). RITE, que
     * es la que calcula con `modoCalculo: 'rite'`, y Baja Tensión (#961), la segunda por decisión de
     * Dirección del 26/10/2026 en #470 porque es la más usada. Las otras 17 pasan una a una, en el
     * orden que fijó Dirección el 31/10/2026 en #962, y cada una se añade a `DISENO_NUEVO_FORMULA` y
     * nada más: la primera, Comunidades de propietarios (#1166).
     *
     * Ésas van por su código y no por su modo porque calculan por fórmula, como las que siguen en el
     * diseño actual. Y sólo mientras sigan siendo de fórmula: su bloque de datos y el desglose del
     * panel están montados sobre esa rama del cálculo.
     */
    usaDisenoNuevo: function (h) {
      const modo = h && h.config && h.config.modoCalculo;
      if (modo === 'rite') return true;
      return !!(h && DISENO_NUEVO_FORMULA.indexOf(h.codigo) >= 0 && (!modo || modo === 'formula'));
    },
    /**
     * Familia (`calor` | `frio` | `mixta`) y modo (`per` | `ini`) de una inspección de RITE. Es la
     * misma función con la que calcula `tarifa-motor.js`: la matriz del diseño nuevo y el cálculo no
     * pueden discrepar.
     */
    familiaYModo: function (clave, params) {
      return TarifaMotor.familiaYModo(clave, params);
    },
    /**
     * El resultado de una herramienta de fórmula en la forma que pinta `CalcResultado` (#961). Como
     * `vistaResultado` con RITE, sólo reordena lo que ya ha calculado `calc()` —`res` y `desglose`—:
     * ningún importe se vuelve a calcular aquí, para que el panel, el PDF y la línea de oferta no
     * puedan decir cifras distintas.
     */
    vistaFormula: function () {
      const r = this.res || {}, d = this.desglose;
      const c = this.CFG, f = this.form || {}, self = this;
      if (!d || !r._p1) {
        return { tpc: '—', tpi: '—', td: '—', total: '—', horas: '', p1: { total: 0, lineas: [] }, p2: null, minutos: [], fallida: null };
      }
      const pct = function (mult) { return Math.round((1 - (mult || 1)) * 10000) / 100; };
      const coma = function (v) { return String(v).replace('.', ','); };
      const lineas = function (trab, minutos, dto, rfs, rn, gastos) {
        const l = [
          { k: 'Trabajo (' + self.fH(minutos) + ', ' + (dto ? '−' + coma(dto) + '%' : 'sin dto.') + ')', v: trab },
          { k: 'Kilómetros · ' + d.km + ' km', v: d.kmcost }
        ];
        if (rfs) l.push({ k: 'Recargo fin de semana ' + c.pctFS + '%', v: rfs });
        if (rn) l.push({ k: 'Recargo nocturno ' + c.pctNocturno + '%', v: rn });
        // Los gastos a cero no se enseñan, como en el diseño actual.
        (gastos || []).forEach(function (g) { if (g.v) l.push({ k: g.k, v: g.v }); });
        return l;
      };
      // Lo que lleva la 2ª de cada parte, que el diseño actual enseña como «TPC2 / TPI2 (40%/40%)».
      const en2a = function (factor) { return self.tiene2a ? ' · en la 2ª, ' + Math.round(factor * 100) + '%' : ''; };
      const tpiDelCampo = d.tpc > 0 && d.tpi > 0 ? ', ' + Math.round(d.tpi / d.tpc * 100) + '% del campo' : '';
      const minutos = [
        { k: 'Campo (TPC)' + en2a(d.f2tpc), v: self.fMin(d.tpc) },
        { k: 'Oficina (TPI' + tpiDelCampo + ')' + en2a(d.f2tpi), v: self.fMin(d.tpi) },
        { k: f.mismo ? 'Desplazamiento · mismo emplazamiento' : 'Desplazamiento · ' + d.km + ' km × ' + coma(c.tdf), v: self.fMin(d.td) }
      ];
      return {
        tpc: r.tpc, tpi: r.tpi, td: r.td, total: r.ttot, horas: r.horas,
        p1: { total: r._p1, lineas: lineas(d.w1, d.U, pct(f.dto), r.rFS1, r.rN1, r.gastos1) },
        p2: this.tiene2a ? { total: r._p2, pct: r.pct2a ? r.pct2a + '% de la 1ª' : '', lineas: lineas(d.w2, d.U2, pct(f.dto2), r.rFS2, r.rN2, r.gastos2) } : null,
        minutos: minutos,
        fallida: null
      };
    },
    /** Lo que dice «Datos suficientes» en una herramienta de fórmula: el tipo elegido y lo tecleado (#961). */
    resumenFormula: function () {
      const self = this, f = this.form || {};
      const partes = this.srvNombre ? [this.srvNombre] : [];
      this.visibleSecciones.forEach(function (sec) {
        (sec.widgets || []).forEach(function (w) {
          if (!w || !w.campo || !self.isVisible(w)) return;
          const v = f[w.campo];
          if (w.tipo === 'number' && v) partes.push((w.label || w.campo) + ': ' + Number(v).toLocaleString('es-ES'));
          else if ((w.tipo === 'checkbox' || w.tipo === 'checkboxCard') && v === true) partes.push(w.label || w.campo);
        });
      });
      return partes.join(' · ');
    },
    /** Vuelve al selector sin cerrar la calculadora: la salida para elegir otra herramienta en la línea. */
    cambiarHerramienta: function () {
      this.cancelarGuardado();
      this.soltarVersiones();
      this.herramienta = null;
      this.resetForm();
    },
    setCtx: function (campo, valor) {
      this.$set(this.ctx, campo, valor);
    },
    /**
     * «Guardar en línea» del diseño nuevo: antes de escribir, enseña lo que se va a escribir y pide
     * confirmación. Lo que se enseña sale del MISMO objeto que se emite al confirmar, así que el
     * diálogo no puede prometer una cifra y dejar otra en la línea.
     */
    pedirGuardado: function () {
      if (!this.contextoOferta || !this.hayPrecio) return;
      const resultado = this.resultadoParaLinea();
      if (!resultado) return;
      this.resultadoPendiente = resultado;
      this.filasGuardado = this.filasDeGuardado(resultado);
      this.dialogoGuardar = true;
    },
    cancelarGuardado: function () {
      this.dialogoGuardar = false;
      this.resultadoPendiente = null;
    },
    confirmarGuardado: function () {
      const resultado = this.resultadoPendiente;
      this.cancelarGuardado();
      if (resultado) this.$emit('tarifa-calculada', resultado);
    },
    filasDeGuardado: function (resultado) {
      const t = resultado.tarifaCalculo || {};
      const fe = this.fmtEur;
      const filas = [];
      // La línea en la que se escribe, con su servicio: la calculadora deja el precio en la línea desde
      // la que se abrió, sea cual sea la herramienta elegida, y eso tiene que verse antes de confirmar.
      const c = this.ctx || {};
      const linea = [c.numLinea != null && c.numLinea !== '' ? 'Línea ' + c.numLinea : '', c.codServicio, c.nombreServicio].filter(Boolean).join(' · ');
      if (linea) filas.push({ k: 'Línea de la oferta', v: linea });
      filas.push({ k: 'Precio de la 1ª inspección', v: fe(resultado.precioUnitario || 0) });
      if (this.tiene2a) {
        filas.push({ k: 'Precio de la 2ª inspección', v: fe(resultado.precioUnitarioSegunda || 0) + (resultado.porcSegunda ? ' (' + resultado.porcSegunda + '% de la 1ª)' : '') });
      }
      filas.push({ k: 'Gastos previstos de la 1ª (km, peajes y dietas)', v: fe((t.gastos1a && t.gastos1a.totalGastos) || 0) });
      if (this.tiene2a) filas.push({ k: 'Gastos previstos de la 2ª', v: fe((t.gastos2a && t.gastos2a.totalGastos) || 0) });
      if (resultado.proveedor) {
        const pr = resultado.proveedor;
        filas.push({ k: 'Subcontrata', v: ([pr.codigo, pr.nombre].filter(Boolean).join(' · ') || 'Sin código') + ' · ' + fe(pr.precio || 0) });
      }
      filas.push({ k: 'Minutos de la 1ª (campo + oficina)', v: (resultado.tiempoEstimadoCampo || 0) + ' + ' + (resultado.tiempoEstimadoOficina || 0) + ' min' });
      filas.push({ k: 'Tipo de inspección', v: this.srvNombre || this.srv || '—' });
      let versiones = 'Herramienta v' + (t.tarifaVersion || 1) + ' · configuración global v' + (t.configGlobalVersion || 1);
      // Reabierta con las versiones de la línea, se guarda con ellas: que no parezca que son las de hoy.
      if (this.reapertura && this.reapertura.estado === 'guardadas') versiones += ' (las de la línea; vigentes: ' + this.versionesTexto.vigentes + ')';
      filas.push({ k: 'Versiones', v: versiones });
      return filas;
    },
    setSrv: function (k) {
      this.srv = k;
      this.$set(this.form, 'srv', k);
      // Si es modo manual (SMEX), establecer TPC/TPI defaults del subtipo
      if (this.herramienta && this.herramienta.config && this.herramienta.config.modoCalculo === 'manual') {
        var subtipos = this.herramienta.subtipos;
        var params = null;
        if (subtipos && subtipos.opciones && subtipos.opciones[k]) {
          params = subtipos.opciones[k].params;
        }
        if (params) {
          if (params.tpcDefault !== undefined) this.$set(this.form, 'tpc', params.tpcDefault);
          if (params.tpiDefault !== undefined) this.$set(this.form, 'tpi', params.tpiDefault);
        }
      }
      this.calc();
    },
    setF: function (campo, val) {
      this.$set(this.form, campo, val);
      this.calc();
    },
    /**
     * ¿Se pinta este widget o esta sección? La condición es una cadena que trae la definición de la
     * herramienta desde Mongo, y se calcula con el intérprete de `expresion-condicion.js` en vez de
     * ejecutarla con `new Function`: el motivo entero está en la cabecera de ese fichero.
     *
     * Lo que no cambia es la respuesta cuando la condición no se puede evaluar: **se pinta**, igual
     * que cuando el `new Function` lanzaba y el `catch` devolvía `true`. Un campo escondido por una
     * condición que nadie puede leer sería un dato que falta sin decirlo.
     */
    isVisible: function (w) {
      if (!w.visible) return true;
      return ExpresionCondicion.cumple(w.visible, { form: this.form }, true);
    },
    toggleTableRow: function(campo, rowKey) {
      var current = this.form[campo] || {};
      var newVal = Object.assign({}, current);
      newVal[rowKey] = !newVal[rowKey];
      this.$set(this.form, campo, newVal);
      this.calc();
    },
    copiarGastos: function () {
      var f = this.form;
      this.$set(this.form, 'sub2', f.sub);
      this.$set(this.form, 'peaj2', f.peaj);
      this.$set(this.form, 'ncom2', f.ncom);
      this.$set(this.form, 'ncen2', f.ncen);
      this.$set(this.form, 'nhot2', f.nhot);
      this.$set(this.form, 'ndes2', f.ndes);
      this.calc();
    },
    // Los formatos del cálculo (#1184): la pantalla escribe las cifras igual que las escribe `res`.
    fN: function (n) { return TarifaMotor.fN(n); },
    fmtEur: function (n) { return TarifaMotor.fmtEur(n); },
    fH: function (m) { return TarifaMotor.fH(m); },
    // Minutos, redondeados como los redondea la maqueta aprobada y como ya los pinta la fila de
    // tiempos: `fN` los daria con dos decimales y «96,00 min» se lee como un fallo.
    fMin: function (m) { return Math.round(m || 0); },

    /**
     * Calcula con lo que hay en pantalla. El cálculo de los cinco modos —fórmula, manual, tabla de
     * precios, clasificación y RITE— vive en `tarifa-motor.js` desde la #1184, sin pantalla, para
     * que los casos de prueba por herramienta y la comparación antes/después calculen con lo mismo
     * que calcula esto. Aquí sólo se le da lo que tiene la pantalla y se guarda lo que devuelve.
     */
    calc: function () {
      const x = TarifaMotor.calcular({
        herramienta: this.herramienta, form: this.form, srv: this.srv,
        params: this.currentParams, cfg: this.CFG, kmmin: this.kmminEfectivo
      });
      if (!x) return;
      this.alertas = x.alertas;
      this.suggManut = x.suggManut;
      // Sólo fórmula y manual dan `desglose`, y los demás modos no lo tocaban: se conserva así, que
      // esto es una extracción. Únicamente lo lee el panel del diseño nuevo de las de fórmula.
      if (x.desglose) this.desglose = x.desglose;
      this.res = x.res;
    },
    /**
     * El presupuesto de RITE con la imagen corporativa: la pestaña PDF de la maqueta aprobada el
     * 09/10/2026 (#468). El marco —cabecera, pie y estilos— es el de `hojaPresupuesto`, el mismo que
     * llevan las demás herramientas desde la #471; aquí sólo va el cuerpo, que es propio de RITE.
     *
     * Ninguna cifra se calcula aquí: todas salen de `res`, que es de donde las saca el panel del
     * resultado, y se formatean con el mismo redondeo. Un presupuesto que dijera otra cifra que la
     * pantalla sería peor que no tener PDF.
     *
     * Lo que la hoja antigua sacaba y ésta no —«Parámetros de cálculo» (precio/hora, km mínimos,
     * coste por km), la línea del descuento y el desglose de minutos— son datos internos de
     * tarificación en un documento que se entrega al cliente; Dirección dio el visto bueno a
     * quitarlos en la #468. El aviso de «Presupuesto incompleto» sí se queda: el papel se envía y el
     * aviso de la pantalla no viaja con él.
     */
    hojaPdfRite: function () {
      const self = this;
      const h = this.herramienta, r = this.res, f = this.form || {}, c = this.CFG;
      const fm = this.familiaYModo(f.srv || this.srv, this.currentParams);
      const familia = { calor: 'Calor', frio: 'Frío', mixta: 'Mixta' }[fm.fam] + ' · ' + (fm.mod === 'ini' ? 'inicial' : 'periódica');
      const esc = escHoja;
      const tabla = function (filas) {
        return '<table class="sh-t">' + filas.map(function (x) { return '<tr><td>' + esc(x[0]) + '</td><td>' + esc(x[1]) + '</td></tr>'; }).join('') + '</table>';
      };
      const desglose = function (p) {
        const filas = [['Trabajo', self.fmtEur(p.trab)], ['Kilómetros', self.fmtEur(p.kmc)]];
        if (p.rfs) filas.push(['Recargo fin de semana', self.fmtEur(p.rfs)]);
        if (p.rn) filas.push(['Recargo nocturno', self.fmtEur(p.rn)]);
        (p.gs || []).forEach(function (g) { filas.push([g[0], self.fmtEur(g[1])]); });
        return tabla(filas);
      };

      // Sólo las filas de la familia elegida y con dato: una inspección de calor no enseña los
      // rangos de frío a cero, ni una de frío los generadores de calor.
      const filas = [['Tipo de inspección', familia]];
      if (fm.fam !== 'calor') {
        this.riteParametros.BANDS.forEach(function (b) { if (f[b.k]) filas.push(['Generadores de frío ' + b.l, f[b.k] + ' ud']); });
        if (f.uta) filas.push(['UTAs / climatizadores', f.uta + ' ud']);
        if (f.emi) filas.push(['Splits, fan-coils, cassettes (informativo)', f.emi + ' ud']);
      }
      if (fm.fam !== 'frio') {
        filas.push(['Generadores de calor', (f.ngc || 0) + ' ud']);
        if (f.kw) filas.push(['Potencia térmica', f.kw + ' kW']);
      }
      if (f.sup) filas.push(['Superficie servida', f.sup.toLocaleString('es-ES') + ' m²']);
      if (f.zon) filas.push(['Edificios o zonas', f.zon]);
      // Un campo añadido desde el editor sale en «Otros datos» de la pantalla; en el papel, también.
      this.riteOtros.forEach(function (w) {
        const v = f[w.campo];
        if (v === undefined || v === null || v === '' || v === false || v === 0) return;
        filas.push([w.label || w.campo, v === true ? 'Sí' : v]);
      });
      filas.push(['Desplazamiento', f.mismo ? 'Mismo emplazamiento' : r.km + ' km ida y vuelta']);

      const fecha = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const fail = r.failD;

      let condiciones = '<div class="sh-sec">Condiciones</div>'
        + tabla([
          // Los minutos dichos igual que en el panel: «96 min de desplazamiento» a secas contradiría
          // los 36 + 60 improductivos que enseña la pantalla para el mismo importe.
          ['Visita fallida por ausencia de frigorista o personal cualificado (desplazamiento: ' + self.fMin(fail.desp) + ' min y '
            + r.km + ' km, más ' + self.fMin(fail.imp) + ' min improductivos)', self.fmtEur(r.fail)],
          ['Precios sin IVA', 'Herramienta v' + (h.version || 1) + ' · Config v' + (c.version || 1)]
        ]);
      if (r.miss && r.miss.length) condiciones += '<div class="sh-miss"><b>Presupuesto incompleto:</b> ' + esc(r.miss.join(' · ')) + '</div>';
      // Un bloque por apartado (#1028): el importe de la 1ª y el de la 2ª van juntos, como en el panel.
      return this.hojaPresupuesto([
        '<div class="sh-title">Inspección RITE · Climatización</div><div class="sh-sub">' + familia + ' · RD 1027/2007, IT 4</div>'
          + '<div class="sh-sec">Datos de la instalación</div>' + tabla(filas),
        '<div class="sh-sec">Tiempos</div><div class="sh-times">'
          + '<div><small>Campo</small><b>' + r.tpc + '</b></div><div><small>Documental</small><b>' + r.tpi + '</b></div>'
          + '<div><small>Desplaz.</small><b>' + r.td + '</b></div><div class="t"><small>Total min</small><b>' + r.ttot + '</b></div></div>',
        '<div class="sh-sec">Importe</div><div class="sh-prices' + (this.tiene2a ? '' : ' una') + '">'
          + '<div class="sh-p one"><div class="k">1ª inspección</div><div class="v">' + self.fmtEur(r.p1.total) + '</div>' + desglose(r.p1) + '</div>'
          + (this.tiene2a ? '<div class="sh-p two"><div class="k">2ª inspección, si hay defectos</div><div class="v">' + self.fmtEur(r.p2.total) + '</div>' + desglose(r.p2) + '</div>' : '')
          + '</div>',
        condiciones
      ], fecha);
    },

    /**
     * El marco del presupuesto de todas las herramientas: cabecera con el logotipo, pie con la
     * empresa y los estilos de la marca (#471). Cada hoja sólo pone sus bloques y, si le hacen falta,
     * reglas propias detrás de las comunes; el marco no se escribe en ningún otro sitio.
     *
     * `bloques` son los apartados de la hoja, en orden. Ninguno se parte entre dos páginas, y el pie
     * va dentro del último para que nunca quede solo en una (#1028): el marco lo impone, y una hoja
     * nueva no puede olvidarlo.
     */
    hojaPresupuesto: function (bloques, fecha, cssPropio) {
      const h = this.herramienta;
      const ref = h.codigo.toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
      const pie = '<div class="sh-foot"><div><b>SIMECAL</b> · Seguridad Industrial, Medioambiente y Calidad, S.L.<br>C/ Manuel Azaña, 39, bajo · 47014 Valladolid</div>'
        + '<div style="text-align:right">simecal@simecal.com<br>www.simecal.com</div></div>';
      const ultimo = bloques.length - 1;
      return '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Presupuesto ' + escHoja(h.nombre) + '</title><style>' + CSS_HOJA_PRESUPUESTO + (cssPropio || '') + '</style></head><body><div class="sheet">'
        // El logotipo va dentro del HTML: la hoja se abre en una ventana en blanco, donde una ruta
        // a `/static/` puede no resolverse y el presupuesto saldría sin marca.
        + '<div class="sh-top"><img src="' + logoApaisado + '" alt="SIMECAL"><div class="sh-meta"><b>Presupuesto de inspección</b><br>Ref. ' + escHoja(ref) + '<br>Fecha ' + fecha
        + '<br>Elaborado por: ' + escHoja(this.autorHoja()) + '<br>Validez 30 días</div></div>'
        + bloques.map(function (b, i) { return '<section class="sh-blk">' + b + (i === ultimo ? pie : '') + '</section>'; }).join('')
        + '</div></body></html>';
    },

    /**
     * Quién firma la hoja, «Elaborado por» de la cabecera (#1028). Si lo que se imprime es el cálculo
     * guardado en la línea, tal cual se reabrió, es quien lo guardó. En cualquier otro caso —desde el
     * menú Tarifas, una línea sin cálculo, o uno reabierto y ya cambiado—, los números son de quien
     * está conectado, y es esa persona quien firma. Lo que no consta no se inventa: el código si falta el
     * nombre, y «—» si falta todo.
     */
    autorHoja: function () {
      const p = this.calculoPrevio;
      if (this.esCalculoGuardado()) return (p.nombreUsuarioCalculo || p.usuarioCalculo || '').trim() || '—';
      const u = usuarioConectado();
      return u.nombre || u.codigo || '—';
    },
    /**
     * Si la calculadora enseña exactamente el cálculo guardado en la línea: reabierto con sus mismas
     * versiones y sin tocar nada después. Las herramientas del diseño actual no reabren el cálculo
     * (#467): con ellas se calcula de nuevo, y eso no es el cálculo guardado.
     */
    esCalculoGuardado: function () {
      const p = this.calculoPrevio, r = this.reapertura;
      return !!(p && r && this.herramienta && this.herramienta.codigo === p.tarifaCodigo
        && (r.estado === 'iguales' || r.estado === 'guardadas')
        && this.formReabierto !== null && this.formReabierto === JSON.stringify(this.form));
    },

    /** Abre la hoja en una ventana nueva y lanza la impresión: es el PDF de todas las herramientas. */
    imprimirHoja: function (html) {
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); setTimeout(function () { win.print(); }, 500); }
      else { this.$f7.dialog.alert('Permite ventanas emergentes para generar el PDF'); }
    },

    openToolbar: function (key) {
      if (this.$refs.toolbar && this.$refs.toolbar.openPanel) {
        this.$refs.toolbar.openPanel(key);
      }
    },
    guardarCalculo: function () {
      var self = this;
      Tarifa.calcularYGuardar({
        modulo: this.herramienta.codigo, srv: this.srv, srvName: this.srvNombre,
        p1: this.res._p1, p2: this.res._p2,
        form: Object.assign({}, this.form), ts: new Date().toISOString(),
        configGlobalVersion: self.CFG.version || 1,
        herramientaVersion: self.herramienta.version || 1,
        fechaCalculo: new Date().toISOString(),
        configSnapshot: { ph: self.CFG.ph, kmcost: self.CFG.kmcost, kmmin: self.CFG.kmmin, tdf: self.CFG.tdf, com: self.CFG.com, cen: self.CFG.cen, hot: self.CFG.hot, des: self.CFG.des, pctFS: self.CFG.pctFS, pctNocturno: self.CFG.pctNocturno }
      }).then(function () {
        self.$f7.toast.create({ text: 'Guardado', closeTimeout: 2000, position: 'center' }).open();
      }).catch(function (e) {
        self.$f7.dialog.alert(e.message || 'Error');
      });
    },
    generarPDF: function () {
      if (!this.herramienta || !this.res) return;
      // RITE y el resto comparten el marco corporativo (#471), pero cada uno tiene su cuerpo. Se elige
      // por el modo de cálculo, como en `calc()`, y no por el código: es lo que decide que `res` traiga
      // el desglose que pinta la hoja de RITE.
      if (this.herramienta.config && this.herramienta.config.modoCalculo === 'rite') this.generarPdfRite();
      else this.generarPdfHoja();
    },
    generarPdfRite: function () {
      if (this.res.p1) this.imprimirHoja(this.hojaPdfRite());
      else this.$f7.dialog.alert('Todavía no hay precio que imprimir: elige el tipo de inspección y rellena los datos de la instalación; el presupuesto sale con el resultado de la derecha.');
    },
    /** Imprime la hoja de las herramientas que no son RITE. */
    generarPdfHoja: function () {
      this.imprimirHoja(this.hojaPdfHoja());
    },
    /**
     * La hoja de todas las herramientas que no son RITE. Desde la #471 va en el marco corporativo de
     * `hojaPresupuesto`, pero conserva sus bloques, sus filas y sus cifras: Dirección decidió el
     * 26/10/2026 que cada herramienta mantiene lo suyo y sólo cambia la presentación. Lo que el marco
     * ya dice —la empresa, la referencia, la fecha y la validez— sale de la cabecera y del pie.
     */
    hojaPdfHoja: function () {
      const fecha = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      // Cada inspección es un bloque aparte (#1028): la 2ª pasa entera a la página siguiente si no
      // cabe, en vez de dejar «Subcontratación 2ª» al pie de la primera.
      const bloques = [this.datosPdfHoja(), this.tiemposPdfHoja(), this.inspeccionPdfHoja(1)];
      if (this.tiene2a) bloques.push(this.inspeccionPdfHoja(2));
      bloques.push(this.parametrosPdfHoja(fecha));
      return this.hojaPresupuesto(bloques, fecha, CSS_HOJA_OTRAS);
    },
    /** Título, subtítulo y «Datos de la instalación». */
    datosPdfHoja: function () {
      const h = this.herramienta, f = this.form, c = this.CFG;
      const srv = this.srvNombre || '', srvNorma = this.srvNorma || '';
      return '<div class="sh-title">' + (h.icono || '') + ' Presupuesto de inspección — ' + h.nombre + '</div>'
        + (srv ? '<div class="sh-sub">' + srv + (srvNorma ? ' · ' + srvNorma : '') + '</div>' : '')
        + (h.subtitulo ? '<div class="hp-nota">' + h.subtitulo + '</div>' : '')
        + '<div class="sh-sec">Datos de la instalación</div><table>' + this.camposPdfHoja()
        + filaPdfHoja('Km (ida+vuelta)', f.mismo ? '0 (mismo emplazamiento)' : f.km + ' km')
        + filaPdfHoja('Descuento 1ª', f.dto === 1 ? 'Sin descuento' : ((1 - f.dto) * 100).toFixed(0) + '%')
        + (f.recargoFS ? filaPdfHoja('Recargo fin de semana', '+' + c.pctFS + '%') : '')
        + (f.recargoNocturno ? filaPdfHoja('Recargo nocturno', '+' + c.pctNocturno + '%') : '')
        + '</table>';
    },
    tiemposPdfHoja: function () {
      const r = this.res;
      return '<div class="sh-sec">Tiempos de inspección</div><div class="sh-times">'
        + tiempoPdfHoja('TPC', r.tpc || 0, 'min') + tiempoPdfHoja('TPI', r.tpi || 0, 'min') + tiempoPdfHoja('T. desp.', r.td || 0, 'min')
        + tiempoPdfHoja('Total', r.ttot || 0, 'min · ' + (r.horas || ''), 't')
        + '</div>';
    },
    /** El bloque de la 1ª o de la 2ª inspección: importe, desglose y fila de total. */
    inspeccionPdfHoja: function (n) {
      const r = this.res, f = this.form, c = this.CFG;
      // El mismo minimo que ha usado `calc()`: con un override por herramienta, el global dejaba el
      // importe de kilometros de la hoja por encima del que suma el total (#379).
      const kmVal = f.mismo ? 0 : Math.max(f.km || 0, this.kmminEfectivo);
      const total = n === 1 ? r.p1Fmt : r.p2Fmt;
      const trabajo = n === 1
        ? filaPdfHoja('Trabajo x dto', r.p1trab || '')
        : filaPdfHoja(r.tpc2Label || '', '') + filaPdfHoja('Trabajo 2ª x dto', r.p2trab || '');
      return '<div class="sh-sec">' + n + 'ª inspección' + (n === 1 ? '' : ' · ' + (r.pct || '')) + '</div>'
        + '<div class="hp-pm' + (n === 1 ? '' : ' dos') + '">' + (total || '---') + '</div>'
        + '<table>' + trabajo + filaPdfHoja('Km (' + r.kmLabel + ')', this.fmtEur(kmVal * c.kmcost)) + this.gastosPdfHoja(n)
        + '<tr class="hp-tot"><td class="dk">Total ' + n + 'ª inspección</td><td class="dv">' + (total || '') + '</td></tr></table>';
    },
    parametrosPdfHoja: function (fecha) {
      const h = this.herramienta, c = this.CFG;
      return '<div class="sh-sec">Parámetros de cálculo</div><table>'
        + '<tr><td class="dk">PH</td><td class="dv">' + c.ph + ' EUR/h</td><td class="dk">Km mínimos</td><td class="dv">' + this.kmminEfectivo + ' km</td><td class="dk">Coste km</td><td class="dv">' + c.kmcost + ' EUR/km</td></tr>'
        + '<tr><td class="dk">Versión herramienta</td><td class="dv">v.' + (h.version || 1) + '</td><td class="dk">Config global</td><td class="dv">v.' + (c.version || 1) + '</td><td class="dk">Fecha</td><td class="dv">' + fecha + '</td></tr>'
        + '</table>'
        + '<div class="hp-pie">Presupuesto orientativo generado el ' + fecha + '. Precios sin IVA.</div>';
    },
    /** Los campos rellenos de la herramienta, como filas de «Datos de la instalación». */
    camposPdfHoja: function () {
      const self = this, f = this.form, secciones = this.herramienta.secciones;
      let html = '';
      (secciones || []).forEach(function (sec) {
        (sec.widgets || []).forEach(function (w) {
          if (!w.campo || f[w.campo] === undefined || f[w.campo] === false || f[w.campo] === 0 || f[w.campo] === '') return;
          let val = f[w.campo];
          if (typeof val === 'boolean') val = val ? 'Sí' : 'No';
          if (typeof val === 'object' && val !== null) val = self.marcadosPdfHoja(secciones, w.campo, val);
          html += filaPdfHoja(w.label || w.campo, val);
        });
      });
      return html;
    },
    /** Los gastos y recargos de la 1ª o de la 2ª inspección: sólo los que tienen dato, como siempre. */
    gastosPdfHoja: function (n) {
      const self = this, f = this.form, c = this.CFG, r = this.res;
      const filas = n === 1 ? [
        [f.sub, 'Subcontratación', f.sub],
        [f.ncom, 'Comidas (' + f.ncom + ' ud x ' + c.com + ')', f.ncom * c.com],
        [f.ncen, 'Cenas (' + f.ncen + ' ud x ' + c.cen + ')', f.ncen * c.cen],
        [f.nhot, 'Hotel (' + f.nhot + ' noches x ' + c.hot + ')', f.nhot * c.hot],
        [f.ndes, 'Desayunos (' + f.ndes + ' ud x ' + c.des + ')', f.ndes * c.des],
        [f.peaj, 'Peajes/Parking', f.peaj],
        [f.recargoFS, 'Recargo fin de semana (' + c.pctFS + '%)', r.rFS1],
        [f.recargoNocturno, 'Recargo nocturno (' + c.pctNocturno + '%)', r.rN1]
      ] : [
        [f.sub2, 'Subcontratación 2ª', f.sub2],
        [f.ncom2, 'Comidas 2ª', f.ncom2 * c.com],
        [f.ncen2, 'Cenas 2ª', f.ncen2 * c.cen],
        [f.nhot2, 'Hotel 2ª', f.nhot2 * c.hot],
        [f.ndes2, 'Desayunos 2ª', f.ndes2 * c.des],
        [f.peaj2, 'Peajes 2ª', f.peaj2],
        [f.recargoFS, 'Recargo FS 2ª (' + c.pctFS + '%)', r.rFS2],
        [f.recargoNocturno, 'Recargo noct. 2ª (' + c.pctNocturno + '%)', r.rN2]
      ];
      return filas.filter(function (x) { return x[0]; }).map(function (x) { return filaPdfHoja(x[1], self.fmtEur(x[2])); }).join('');
    },
    /**
     * Las casillas marcadas de un campo de varias opciones, como las pinta la hoja antigua: con la
     * etiqueta de su fila si el widget la tiene. Va aparte para que `generarPdfHoja` no anide cinco
     * funciones (Sonar S2004).
     */
    marcadosPdfHoja: function (secciones, campo, val) {
      const keys = Object.keys(val).filter(function (k) { return val[k] === true; });
      if (!keys.length) return '---';
      let widget = null;
      (secciones || []).forEach(function (sec) {
        (sec.widgets || []).forEach(function (ww) { if (ww.campo === campo && ww.rows) widget = ww; });
      });
      if (!widget) return keys.map(function (k) { return '&#10003; ' + k; }).join(', ');
      return keys.map(function (k) {
        const row = widget.rows.find(function (x) { return x.key === k; });
        return '&#10003; ' + (row ? row.label : k);
      }).join('<br>');
    },
    aplicarYEmitir: function() {
      const resultado = this.resultadoParaLinea();
      if (resultado) this.$emit('tarifa-calculada', resultado);
    },
    /**
     * Lo que se escribe en la línea de oferta. Estaba dentro de `aplicarYEmitir`, que lo montaba y
     * lo emitía de una vez; se separa (#467) porque el diseño nuevo tiene que ENSEÑARLO antes de
     * escribirlo, y enseñar una copia montada aparte podría no coincidir con lo escrito. El diseño
     * actual sigue llamando a `aplicarYEmitir` y hace exactamente lo de antes.
     */
    resultadoParaLinea: function() {
      var self = this;
      if (!self.herramienta || !self.res || !self.res._p1) return null;
      var h = self.herramienta, r = self.res, f = self.form, c = self.CFG;

      // `gastos1a`/`gastos2a` son el desglose de lo que ya va dentro de `precio1a`/`precio2a`, no un
      // importe que se sume aparte, asi que los kilometros tienen que ser los MISMOS que ha cobrado
      // `calc()`: el minimo efectivo de la herramienta, y cero si es el mismo emplazamiento (#379).
      const kmin = self.kmminEfectivo;
      const kmEmitidos = f.mismo ? 0 : Math.max(f.km || 0, kmin || 60);

      function buildGastos(s) {
        s = s || '';
        const km = kmEmitidos;
        const g = { kilometros: f.km||0, importeKm: parseFloat((km*(c.kmcost||0.29)).toFixed(2)), peajes: f['peaj'+s]||0, parking: 0, desayuno: f['ndes'+s]||0, importeDesayuno: parseFloat(((f['ndes'+s]||0)*(c.des||4)).toFixed(2)), comida: f['ncom'+s]||0, importeComida: parseFloat(((f['ncom'+s]||0)*(c.com||14)).toFixed(2)), cena: f['ncen'+s]||0, importeCena: parseFloat(((f['ncen'+s]||0)*(c.cen||14)).toFixed(2)), hotel: f['nhot'+s]||0, importeHotel: parseFloat(((f['nhot'+s]||0)*(c.hot||60)).toFixed(2)), finDeSemana: !!f.recargoFS, horasNocturnas: !!f.recargoNocturno, totalGastos: 0 };
        g.totalGastos = parseFloat(((g.importeKm||0)+(g.peajes||0)+(g.importeDesayuno||0)+(g.importeComida||0)+(g.importeCena||0)+(g.importeHotel||0)).toFixed(2));
        return g;
      }
      var g1 = buildGastos('');
      var g2 = buildGastos('2');
      const quien = usuarioConectado();
      var tarifaCalculo = {
        tarifaId: h.id || h._id, tarifaCodigo: h.codigo, tarifaVersion: h.version || 1,
        // Con que version de la configuracion global se calculo. El precio hora, el minimo de
        // kilometros, los minutos por kilometro y el coste por kilometro salen de ahi y estan
        // versionados, asi que sin este dato el snapshot no se puede reproducir: dos lineas con el
        // mismo importe y la misma version de herramienta pueden venir de dos tarifas distintas
        // (decision de direccion del 09/10/2026, #466). `parametros` ya guarda lo tecleado.
        configGlobalVersion: c.version || 1,
        parametros: JSON.parse(JSON.stringify(f)),
        tiempoCampo1a: r.tpc||0, tiempoOficina1a: r.tpi||0, precio1a: r._p1||0, gastos1a: g1,
        porcSegunda: r.pct2a||0, tiempoCampo2a: r.tpc2||0, tiempoOficina2a: r.tpi2||0, precio2a: r._p2||0, gastos2a: g2,
        resumenCalculo: h.nombre + ' | TPC:' + (r.tpc||0) + ' | 1a:' + (r.p1Fmt||'0') + ' | 2a:' + (r.p2Fmt||'0'),
        fechaCalculo: new Date().toISOString(),
        // Quién aplica la tarifa: el código y, desde la #1028, también el nombre, que es lo que
        // enseñan la línea y «Elaborado por» del PDF.
        usuarioCalculo: quien.codigo,
        nombreUsuarioCalculo: quien.nombre,
        contexto: JSON.parse(JSON.stringify(self.ctx))
      };

      var caracteristicas = [];
      if (h.secciones) {
        h.secciones.forEach(function(sec) {
          if (!sec.widgets) return;
          sec.widgets.forEach(function(w) {
            var val = f[w.campo];
            if (val !== undefined && val !== null && val !== '' && val !== 0 && val !== false) {
              if (w.tipo === 'radioCard' || w.tipo === 'levelSelector' || w.tipo === 'select') {
                var opt = w.options ? w.options.find(function(o) { return o.key === val; }) : null;
                caracteristicas.push((w.label || w.campo) + ': ' + (opt ? opt.label || opt.key : val));
              } else if (w.tipo === 'checkbox' || w.tipo === 'togglePair') {
                if (val === true || val === 'true') caracteristicas.push(w.label || w.campo);
              } else if (w.tipo === 'number') {
                caracteristicas.push((w.label || w.campo) + ': ' + val);
              }
            }
          });
        });
      }
      if (self.srv) {
        var subOpts = self.herramienta.subtipos && self.herramienta.subtipos.opciones ? self.herramienta.subtipos.opciones : {};
        var subLabel = subOpts[self.srv] ? subOpts[self.srv].label || self.srv : self.srv;
        caracteristicas.unshift('Tipo: ' + subLabel);
      }
      if (f.recargoFS) caracteristicas.push('Fin de semana (+30%)');
      if (f.recargoNocturno) caracteristicas.push('Horas nocturnas (+35%)');

      var totalCuadros = 0;
      if (h.secciones) {
        h.secciones.forEach(function(sec) {
          if (!sec.widgets) return;
          sec.widgets.forEach(function(w) {
            if (w.label && w.label.toLowerCase().indexOf('cuadro') > -1 && w.tipo === 'number') {
              totalCuadros += (parseInt(f[w.campo]) || 0);
            }
          });
        });
      }

      var cantidadFromCalculador = null;
      var codigoHerramienta = (h.codigo || '').toLowerCase();
      var mapaCantidad = { 'ae': 'ascensor', 'aega': 'grua', 'aegt': 'grua', 'eiciae': 'ascensor', 'smmin': 'maquina', 'sm': 'maquina', 'ip': 'enterrado' };
      var buscarLabel = mapaCantidad[codigoHerramienta] || null;
      if (buscarLabel) {
        var buscarNorm = buscarLabel.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        var encontrado = false;
        if (h.secciones) {
          h.secciones.forEach(function(sec) {
            if (encontrado || !sec.widgets) return;
            sec.widgets.forEach(function(w) {
              if (encontrado) return;
              var labelNorm = (w.label || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
              if (labelNorm.indexOf(buscarNorm) >= 0 && w.tipo === 'number') {
                var val = parseFloat(f[w.campo]) || 0;
                if (val > 0) { cantidadFromCalculador = val; }
                encontrado = true;
              }
            });
          });
        }
      }

      var precioUnitario = parseFloat((tarifaCalculo.precio1a || 0).toFixed(2));
      var precioUnitarioSegunda = parseFloat((tarifaCalculo.precio2a || 0).toFixed(2));
      var tiempoEstimadoCampo = tarifaCalculo.tiempoCampo1a;
      var tiempoEstimadoOficina = tarifaCalculo.tiempoOficina1a;
      var cantidadAplicada = parseFloat(self.ctx.cantidad) || 1;
      if (cantidadFromCalculador !== null && cantidadFromCalculador > 0) {
        cantidadAplicada = cantidadFromCalculador;
        if (cantidadFromCalculador > 1) {
          if (precioUnitario) precioUnitario = parseFloat((precioUnitario / cantidadFromCalculador).toFixed(2));
          if (precioUnitarioSegunda > 0) precioUnitarioSegunda = parseFloat((precioUnitarioSegunda / cantidadFromCalculador).toFixed(2));
          if (tiempoEstimadoCampo > 0) tiempoEstimadoCampo = Math.round(tiempoEstimadoCampo / cantidadFromCalculador);
          if (tiempoEstimadoOficina > 0) tiempoEstimadoOficina = Math.round(tiempoEstimadoOficina / cantidadFromCalculador);
        }
      }
      var importeSegunda = parseFloat((precioUnitarioSegunda * cantidadAplicada).toFixed(2));

      var proveedor = null;
      if ((f.sub && f.sub > 0) || (self.ctx.codSubcontrata && self.ctx.codSubcontrata !== '')) {
        proveedor = { precio: f.sub || 0 };
        if (self.ctx.codSubcontrata) proveedor.codigo = self.ctx.codSubcontrata;
        if (self.ctx.nombreSubcontrata) proveedor.nombre = self.ctx.nombreSubcontrata;
      }

      var resultado = {
        tarifaCalculo: tarifaCalculo,
        precioUnitario: precioUnitario,
        precioUnitarioSegunda: precioUnitarioSegunda,
        porcSegunda: tarifaCalculo.porcSegunda || 0,
        importeSegunda: importeSegunda,
        tiempoEstimadoCampo: tiempoEstimadoCampo,
        tiempoEstimadoOficina: tiempoEstimadoOficina,
        previsionGastos: { kilometros: f.km || 0, peajes: g1.peajes, parking: 0, desayuno: g1.importeDesayuno, comida: g1.importeComida, cena: g1.importeCena, hotel: g1.importeHotel, finDeSemana: g1.finDeSemana, horasNocturnas: g1.horasNocturnas },
        trabajoRealizar: caracteristicas.join(' | '),
        numUnidadesOfertadas: totalCuadros > 0 ? totalCuadros : null,
        cantidad: cantidadFromCalculador,
        proveedor: proveedor,
        precio1a: precioUnitario,
        tiempoCampo: tiempoEstimadoCampo,
        tiempoOficina: tiempoEstimadoOficina
      };
      return resultado;
    },
    accionGuardar: function() {
      if (this.contextoOferta) { this.aplicarYEmitir(); return; }
      this.guardarCalculo();
    },
    openGuiaUso: function() {
      var self = this;
      self.popupGuiaOpened = true;
      self.guiaError = null;
      if (self.guiaContenido) return;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que lo soltaba.
      cerrojoDeLaSesion(self, 'guiaLoading');
      TarifaContenido.listar({ modulo: 'general', tipo: 'GUIA' }).then(function(res) {
        self.guiaLoading = false;
        var lista = res.list || res.data || res || [];
        if (Array.isArray(lista) && lista.length > 0) {
          self.guiaContenido = lista[0];
        } else {
          self.guiaError = 'No se encontró la Guía de Uso. Contacte con el administrador.';
        }
      }).catch(function(err) {
        self.guiaLoading = false;
        self.guiaError = 'Error cargando la guía: ' + (err.message || 'desconocido');
      });
    },
    entrarModoEdicion: function() {
      if (!this.guiaContenido) return;
      this.guiaEditContenido = this.guiaContenido.contenido;
      this.guiaEditMode = true;
    },
    cancelarEdicion: function() {
      var self = this;
      if (self.guiaEditContenido !== self.guiaContenido.contenido) {
        self.$f7.dialog.confirm('Hay cambios sin guardar. ¿Seguro que quieres cancelar?', function() {
          self.guiaEditMode = false;
          self.guiaEditContenido = '';
        });
      } else {
        self.guiaEditMode = false;
        self.guiaEditContenido = '';
      }
    },
    // El «Guardar» de la barra hace lo mismo que el «Guardar todo» del editor. Antes guardaba
    // `guiaEditContenido`, que el editor no actualiza nunca: lo que se hubiera cambiado en los
    // puntos se descartaba en silencio y el aviso decía «guardada correctamente».
    guardarGuia: function() {
      if (this.$refs.guiaEditor) this.$refs.guiaEditor.guardar();
    },
    onGuiaSaved: function(htmlFinal, done) {
      var self = this;
      const guia = self.guiaContenido;
      // La API contesta `id`; `_id` se deja por si llega en el formato de Mongo, como en
      // CalculadorToolbar.
      const id = guia && (guia.id || (guia._id && guia._id.$oid ? guia._id.$oid : guia._id));
      if (!id) {
        self.$f7.dialog.alert('Error: no se ha identificado el documento.');
        if (done) done();
        return;
      }
      // #663 · El cerrojo de la sesión, como el resto de la pantalla: si la sesión cambia en vuelo,
      // la frontera calla el `then` y el «Guardar» de la barra se quedaría apagado.
      cerrojoDeLaSesion(self, 'guiaGuardando');
      // Se manda el documento entero y no solo el HTML: la API lo sustituye completo
      // (`repository.save`), y sin `modulo` ni `tipo` el siguiente «i» ya no encontraría la guía.
      TarifaContenido.actualizar(id, Object.assign({}, guia, {
        contenido: htmlFinal,
        modificationDate: new Date()
      })).then(function() {
        self.guiaGuardando = false;
        self.guiaContenido.contenido = htmlFinal;
        self.guiaEditMode = false;
        self.$f7.toast.create({ text: '✅ Guía guardada correctamente', closeTimeout: 3000, position: 'bottom' }).open();
        if (done) done();
      }).catch(function(err) {
        self.guiaGuardando = false;
        self.$f7.dialog.alert('Error al guardar: ' + (err.message || 'desconocido'));
        if (done) done();
      });
    }
  }
};
</script>

<style scoped>
.v3{display:flex;flex-direction:column;height:calc(100vh - 44px);overflow:hidden;background:#f0f2f5}
.ctx-panel{border:1px solid #cbd5e1;border-radius:8px;overflow:hidden;margin-top:0}
.ctx-hdr{display:flex;justify-content:space-between;align-items:center;padding:6px 10px;background:linear-gradient(135deg,#1a2744,#2d3a56);cursor:pointer;font-size:10px;font-weight:700;color:#fff;letter-spacing:.06em}
.ctx-body{padding:8px 10px;background:#fafbfc}
.ctx-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:5px}
.ctx-field label{display:block;font-size:8px;font-weight:700;color:#1a2744;text-transform:uppercase;letter-spacing:.04em;margin-bottom:1px}
.ctx-field input{width:100%;padding:4px 6px;border:2px solid #e2e8f0;border-radius:4px;font-size:11px;font-weight:600;color:#1e293b;background:#fff;box-sizing:border-box}
.ctx-ro input{background:#f0f4ff;color:#475569;border-color:#dbeafe}
.ctx-ed input{background:#fff;border-color:#E8901A}
@media(max-width:600px){.ctx-grid{grid-template-columns:1fr}}
.tb-nav{padding:3px 8px !important;border-radius:4px;font-size:10px !important;font-weight:700;color:#fff !important;margin-left:3px;min-width:auto;line-height:20px;display:inline-block}
.tb-guia{width:24px;height:24px;border-radius:50%;background:#00b894;color:#fff;font-weight:bold;font-size:13px;border:none;cursor:pointer;margin-left:6px;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,184,148,.4);transition:transform .15s}
.tb-guia:hover{transform:scale(1.1);background:#00d4aa}
.navbar .right{display:flex;align-items:center;flex-wrap:wrap;overflow:visible !important}
.navbar .right .link.tb-nav{padding:3px 8px;border-radius:4px;font-size:10px;font-weight:700;color:#fff;margin-left:3px}
.main{display:flex;flex:1;gap:0;overflow:hidden;min-height:0}
.col-left{flex:1;overflow-y:auto;padding:8px;display:flex;flex-direction:column}
.col-left::-webkit-scrollbar{width:5px}
.col-left::-webkit-scrollbar-thumb{background:#b0b8c4;border-radius:3px}
.cols2{display:flex;gap:8px;flex:1;min-height:0;align-items:stretch}
.col-a,.col-b{flex:1;display:flex;flex-direction:column;gap:7px;min-height:0;background:#f0f2f5}
.col-right{width:280px;flex-shrink:0;background:#1a2744;display:flex;flex-direction:column;overflow:hidden}
.rh{padding:8px 10px;border-bottom:1px solid rgba(255,255,255,.08);flex-shrink:0}
.rh-t{font-size:12px;font-weight:700;color:#fff;letter-spacing:.08em}
.rh-s{font-size:9px;color:rgba(255,255,255,.45);margin-top:2px}
.r-body{flex:1;overflow-y:auto;min-height:0}
.r-body::-webkit-scrollbar{width:4px}
.r-body::-webkit-scrollbar-thumb{background:rgba(255,255,255,.15);border-radius:2px}
.r-foot{padding:8px;flex-shrink:0;display:flex;gap:5px;border-top:1px solid rgba(255,255,255,.08)}
.rb{flex:1;padding:6px;border:none;border-radius:5px;font-size:9px;font-weight:700;cursor:pointer;font-family:inherit}
.rb-p{background:#E8901A;color:#fff}
.rb-p:hover{background:#f5a020}
.rb-s{background:rgba(255,255,255,.08);color:rgba(255,255,255,.6);border:1px solid rgba(255,255,255,.12)}
.rb-s:hover{background:rgba(255,255,255,.15);color:#fff}
.trow{display:grid;grid-template-columns:repeat(4,1fr);gap:4px;padding:6px 8px}
.tb,.trow .tb{background:rgba(255,255,255,.05);border-radius:5px;padding:5px 3px;text-align:center}
.t-acc{background:rgba(232,144,26,.18)!important;border:1px solid rgba(232,144,26,.25)}
.tl{font-size:8px;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.05em}
.tv{font-size:16px;font-weight:700;color:#fff}
.t-big{font-size:18px;color:#E8901A}
.tu{font-size:8px;color:rgba(255,255,255,.25)}
.tinfo{display:flex;gap:10px;padding:5px 8px;border-bottom:1px solid rgba(255,255,255,.06);flex-wrap:wrap;font-size:9px;color:rgba(255,255,255,.4)}
.tinfo b{color:rgba(255,255,255,.8)}
.rp{padding:10px}
.rp-1{border-bottom:1px solid rgba(255,255,255,.07)}
.rp-lbl{font-size:9px;font-weight:700;color:rgba(255,255,255,.35);letter-spacing:.1em;margin-bottom:4px}
.pill{background:rgba(255,255,255,.08);border-radius:8px;padding:1px 6px;font-size:8px;color:rgba(255,255,255,.5);margin-left:4px}
.rp-price{font-size:28px;font-weight:800;color:#fff;letter-spacing:-.02em}
.rp-price.rp-2{font-size:21px;color:#7dd3fc}
.rp-dg{margin-top:6px}
.dl{display:flex;justify-content:space-between;padding:2px 4px 2px 0;border-bottom:1px solid rgba(255,255,255,.03);font-size:9px}
.dl span:first-child{color:rgba(255,255,255,.35)}
.dl span:last-child{font-weight:600;color:rgba(255,255,255,.8)}
.dl-t{padding:4px 0;margin-top:3px;border-top:1px solid rgba(255,255,255,.1);border-bottom:none}
.dl-t span:first-child{color:rgba(255,255,255,.55);letter-spacing:.05em}
.dl-t span:last-child{font-size:11px;color:#fff}
.dl-t2 span:last-child{color:#7dd3fc}
/* Estado de los datos y visita fallida de RITE (#466). Van dentro del panel oscuro del resultado,
   asi que no reutilizan `.aw`/`.ai`, que son de fondo claro y estan pensados para la columna izquierda. */
.r-miss{margin:8px;padding:7px 9px;border-radius:5px;background:rgba(234,179,8,.14);border:1px solid rgba(234,179,8,.45)}
.r-miss-t{font-size:9px;font-weight:700;color:#fde68a;letter-spacing:.04em;margin-bottom:3px}
.r-miss-l{font-size:9px;line-height:1.5;color:rgba(255,255,255,.75)}
.rp-price.rp-f{font-size:21px;color:#fca5a5}
.rp-note{margin-top:5px;font-size:8px;line-height:1.45;color:rgba(255,255,255,.35)}
.r-sep{height:1px;background:rgba(255,255,255,.08)}
.cd{background:#fff;border:.5px solid #e2e8f0;border-radius:7px;overflow:hidden}
.cd-grow{flex:1}
.ch{padding:5px 8px;font-size:10px;font-weight:700;color:#fff}
.ch-navy{background:#1a2744}
.ch-green{background:#166534}
.ch-blue{background:#1e3a5f}
.ch-gray{background:#374151}
.ch-brown{background:#92400e}
.cb{padding:7px 8px}
.sg{display:grid;grid-template-columns:repeat(3,1fr);gap:4px}
.sc{display:flex;flex-direction:column;align-items:center;gap:1px;padding:6px 4px;border:1.5px solid #e2e8f0;border-radius:6px;background:#f8fafc;cursor:pointer;transition:all .12s;font-family:inherit;text-align:center}
.sc:hover{border-color:#E8901A;background:#fffbeb}
.sc.act{border-color:#E8901A;background:linear-gradient(135deg,#fffbeb,#fef3c7);color:#92400e}
.sc-ico{font-size:15px}
.sc-name{font-size:9px;font-weight:700;color:#475569;line-height:1.2}
.sc.act .sc-name{color:#92400e}
.sc-norma{font-size:8px;color:#94a3b8}
.info-box{margin-top:5px;padding:5px 7px;background:#f0f9ff;border-left:2px solid #38bdf8;border-radius:4px;font-size:9px;color:#0369a1;line-height:1.4}
.g1{display:grid;grid-template-columns:1fr;gap:6px}
.g2{display:grid;grid-template-columns:repeat(2,1fr);gap:6px}
.g3{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.g4{display:grid;grid-template-columns:repeat(4,1fr);gap:5px}
.g5{display:grid;grid-template-columns:repeat(5,1fr);gap:4px}
.fi{display:flex;flex-direction:column;gap:2px}
.fi label{font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.05em}
.fi input,.fi select{border:1px solid #d1d9e0;border-radius:4px;padding:4px 6px;font-size:11px;font-family:inherit;color:#1e293b;background:#f8fafc;outline:none;width:100%;box-sizing:border-box}
.fi input:focus,.fi select:focus{border-color:#E8901A;background:#fff}
.fi input:disabled{background:#f1f5f9;color:#94a3b8}
.ht{font-size:8px;color:#94a3b8}
.rq{color:#e53e3e}
.sep{height:1px;background:#e2e8f0;margin:6px 0}
.stit{font-size:8px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}
.uf{display:flex;align-items:center;gap:3px}
.uf input{flex:1;min-width:0}
.uf span{font-size:8px;color:#94a3b8;white-space:nowrap}
.ck{display:flex;align-items:center;gap:4px;cursor:pointer;padding:3px 5px;border:1px solid #e2e8f0;border-radius:5px;background:#f8fafc;font-size:9px;font-weight:600;color:#374151;user-select:none;transition:all .1s}
.ck:hover{border-color:#E8901A;background:#fffbeb}
.ck input{cursor:pointer;accent-color:#E8901A;width:11px;height:11px}
.aw{padding:5px 7px;border-radius:5px;font-size:9px;line-height:1.4;background:#fffbeb;border:1px solid #fde68a;color:#92400e;margin-bottom:5px}
.ai{padding:5px 7px;border-radius:5px;font-size:9px;line-height:1.4;background:#f0f9ff;border:1px solid #bae6fd;color:#0369a1}
.bcopy{font-size:8px;font-weight:700;background:#e8f0fe;border:1px solid #94a3b8;border-radius:4px;padding:2px 7px;cursor:pointer;color:#1e40af;font-family:inherit}
.sel-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px}
.sel-card{display:flex;flex-direction:column;align-items:center;gap:4px;padding:14px 8px;border:1.5px solid #e2e8f0;border-radius:8px;background:#f8fafc;cursor:pointer;font-size:12px;font-weight:600;color:#1e293b;transition:all .15s;font-family:inherit;text-align:center}
.sel-card:hover{border-color:#E8901A;background:#fffbeb}
@media(max-width:900px){.main{flex-direction:column}.col-right{width:100%;max-height:45vh}.cols2{flex-direction:column}}
</style>

<style>
/* Popup Guia de Uso (no-scoped: f7-popup teleporta al body) */
.popup-guia-uso { --f7-popup-tablet-width: 95vw; --f7-popup-tablet-height: 95vh; }
/* Fondo claro de la maqueta aprobada (#469): la guía es una tarjeta blanca con cabecera azul
   corporativo, y este bloque, al no ser scoped, pinta también detrás de su HTML. Con el azul
   oscuro de antes la tarjeta quedaba encajada en un marco negro. */
.popup-guia-uso .guia-container { background: #F3F6F9; min-height: 100%; padding: 0; }
.popup-guia-uso .guia-loading { padding: 40px; text-align: center; color: #5B6B7D; font-size: 14px; display: flex; align-items: center; justify-content: center; }
.popup-guia-uso .guia-error { padding: 40px; text-align: center; color: #B42318; font-size: 14px; }
.popup-guia-uso .guia-html-render { width: 100%; padding: 16px; box-sizing: border-box; }
.popup-guia-uso .btn-editar-guia { --f7-button-bg-color: #ffa500 !important; --f7-button-text-color: #0a1128 !important; font-weight: 700; margin-right: 6px !important; }
.popup-guia-uso .btn-guardar-guia { --f7-button-bg-color: #00b894 !important; --f7-button-text-color: #ffffff !important; font-weight: 700; margin-right: 6px !important; }
.popup-guia-uso .btn-cancelar-guia { --f7-button-bg-color: #6c757d !important; --f7-button-text-color: #ffffff !important; margin-right: 6px !important; }
</style>
